import { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ChevronLeft, ExternalLink, Video, CheckCircle2, XCircle,
  DollarSign, Eye, Clock, Search,
  ArrowUp, ArrowDown, ArrowUpDown, ArrowRight, CalendarClock, AlertTriangle,
  Send, Hourglass, Ban, ClipboardCheck, History, Wallet, RotateCcw, ArrowLeftRight, Flag, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { logAdminAction, logUpdate } from '@/lib/audit-log';
import { refundState, partialRefundCents, fundingLabel, usd, isSystemActor, refundActorLabel, extensionRequestPending } from '@/lib/refunds';
import { payoutClosed, payoutState, payoutSubmissionCounts } from '@/lib/payouts';
import { recordHirePayment } from '@/lib/pay-hire';
import { latestTrolleyPayout, TROLLEY_PAYOUT_COLUMNS, type TrolleyPayout } from '@/lib/trolley-payouts';
import { TrolleyPayoutMethodToggle, TrolleyPayoutSendForm, TrolleyPayoutStatusCard, type PayMethod } from '@/components/TrolleyPayout';
import { refundHireToCredit } from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { ViralRemakeBadge, isViralRemake } from '@/components/ViralRemakeBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogContent, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { DataPagination } from '@/components/DataPagination';
import { HireFeedbackCard } from '@/components/HireFeedbackCard';
import { SampleReceiptCard } from '@/components/SampleReceiptCard';
import { VideoFlagsCard } from '@/components/VideoFlagsCard';
import { VideoFlagBadge } from '@/components/VideoFlagBadge';
import { AdminVideoFlagCell } from '@/components/AdminVideoFlagCell';
import { ADMIN_FLAG_COLUMNS, type AdminFlagFields } from '@/lib/admin-video-flag';
import { BrandReactionBadge, BrandSentimentSummary } from '@/components/BrandReactionBadge';
import {
  FLAG_STATUS_META, VIDEO_FLAG_COLUMNS, isFlagActive,
  MAX_FLAG_RESOLUTION_NOTES, MIN_FLAG_RESOLUTION_NOTES, type VideoFlagFields,
} from '@/lib/video-flags';
import { resolveVideoFlag } from '@/lib/resolve-video-flag';
import { deadlineDaysForPackage, graceHoursRemaining, SUBMISSION_GRACE_PERIOD_HOURS, isPastGracePeriod, isWithinGracePeriod } from '@/lib/deadlines';
import {
  type RejectReasonTag, rejectReasonTagsFor, rejectReasonLabels,
  MIN_REJECT_NOTE, MIN_REJECT_OTHER_NOTE, MAX_REJECT_OTHER_NOTE,
  rejectBriefReminder, rejectNotValidReasons, rejectNotePlaceholder, otherReasonPlaceholder,
} from '@/lib/rejectReasons';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

type SortField = 'submitted_at' | 'views' | 'gmv_cents' | 'orders' | 'units_sold' | 'product_match';
type SortDir = 'asc' | 'desc';

/** Extends VideoFlagFields because a brand's flag lives on this same row — the
 *  brand_flag_* columns on cr_video_submissions. See lib/video-flags.ts. */
type VideoSubmission = VideoFlagFields & AdminFlagFields & {
  id: string;
  hire_id: string;
  tiktok_video_url: string;
  tiktok_video_id: string | null;
  product_match: boolean | null;
  // The product actually tagged in the video (set regardless of match
  // outcome) — see 20260721000006_cr_video_submissions_submitted_product.sql.
  // Null on videos submitted before that migration (no backfill) — fall
  // back to matched_product_id/name below for those.
  submitted_product_id: string | null;
  submitted_product_name: string | null;
  matched_product_id: string | null;
  matched_product_name: string | null;
  review_status: 'approved' | 'rejected' | null;
  /** Who decided: BMC ops, the hiring brand, or nobody (the 7-day window
   *  expired). See creator-app 20260916000000_brand_video_review.sql. */
  review_source?: 'admin' | 'brand' | 'auto' | null;
  /** Which thumb the BRAND approved it with — like, neutral or dislike. All
   *  three are approvals; see components/BrandReactionBadge. NULL = nobody was
   *  asked (BMC decided it, or it auto-approved). */
  brand_reaction?: 'like' | 'neutral' | 'dislike' | null;
  /** Why the brand disliked a video it still approved. Set only alongside
   *  brand_reaction = 'dislike'; never shown to the creator. */
  brand_reaction_note?: string | null;
  reject_reason: string | null;
  /** Category tags picked alongside reject_reason — never shown to the
   *  creator, a queryable companion for BMC. null on anything not rejected, or
   *  a rejection made before 2026-09-23. See lib/rejectReasons.ts. */
  reject_reason_tags?: string[] | null;
  /** What "other" means, only present when reject_reason_tags contains
   *  'other'. Never shown to the creator, same visibility as the tags
   *  themselves. null on anything not rejected, or a rejection made before
   *  2026-09-24. */
  reject_reason_other?: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  ownership_verified: boolean | null;
  ownership_verified_at: string | null;
  views: number | null;
  gmv_cents: number | null;
  orders: number | null;
  units_sold: number | null;
  click_through_rate: number | null;
  last_synced_at: string | null;
  last_sync_error: string | null;
  submitted_at: string;
  deleted: boolean;
};

/** A submission TikTok no longer serves. Displayed to admins for context only —
 *  nothing here feeds a count, a payout or a refund. */
type DeletedVideo = {
  id: string;
  tiktok_video_url: string;
  tiktok_video_id: string | null;
  /** The decision it carried before it vanished. An 'approved' row here is the
   *  interesting case: it had already counted, and losing it is what sends a
   *  completed hire back to 'confirmed'. */
  review_status: 'approved' | 'rejected' | null;
  review_source?: 'admin' | 'brand' | 'auto' | null;
  reviewed_at: string | null;
  submitted_at: string;
  /** When the sync first found it gone. Null on rows marked deleted before the
   *  column existed. */
  deleted_at: string | null;
  last_synced_at: string | null;
  views: number | null;
  gmv_cents: number | null;
};

type VideoStat = {
  product_match: boolean | null;
  // Multi-product campaigns: which product this video is actually for. Read by
  // the "Videos by product" breakdown below — which has to cover every video on
  // the hire, so these live on the unpaginated stats query, not just the table's
  // own page. submitted_* is what TikTok reported the video tagged; matched_* is
  // the campaign product it resolved to. Videos submitted before submitted_*
  // existed only have matched_*.
  submitted_product_id: string | null;
  submitted_product_name: string | null;
  matched_product_id: string | null;
  matched_product_name: string | null;
  review_status: 'approved' | 'rejected' | null;
  // Only meaningful when review_status is 'rejected' — when the rejection
  // happened, used by RejectedVideoDeadlineBanner to tell "rejected right as
  // the deadline ran out" apart from "rejected ages ago, creator just never
  // replaced it" (see that component for why the distinction matters).
  reviewed_at: string | null;
  /** Who decided: BMC ops, the hiring brand, or nobody (the 7-day window
   *  expired). See creator-app 20260916000000_brand_video_review.sql. */
  review_source?: 'admin' | 'brand' | 'auto' | null;
  /** Which thumb the BRAND approved it with — like, neutral or dislike. All
   *  three are approvals; see components/BrandReactionBadge. NULL = nobody was
   *  asked (BMC decided it, or it auto-approved). */
  brand_reaction?: 'like' | 'neutral' | 'dislike' | null;
  /** Why the brand disliked a video it still approved. Set only alongside
   *  brand_reaction = 'dislike'; never shown to the creator. */
  brand_reaction_note?: string | null;
  views: number | null;
  gmv_cents: number | null;
  orders: number | null;
};

/** One row of cr_campaign_products — the campaign's own product list. */
type CampaignProductRow = {
  id: string;
  product_id: string | null;
  product_name: string | null;
  product_image_urls: string[] | null;
  sort_order: number | null;
};

type Hire = {
  id: string;
  campaign_id: string;
  // Set when the hire came from a creator application. The DB trigger that closes
  // a settled hire closes this application too, which is what lets the creator
  // re-apply (see 20260807040000_close_hire_engagement_trigger.sql).
  application_id: string | null;
  creator_id: string;
  status: string;
  package_size: number;
  creator_rate_cents: number;
  videos_required: number;
  confirmed_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
  deadline_at: string | null;
  payment_status: 'unpaid' | 'paid';
  payment_transaction_id: string | null;
  payment_amount_cents: number | null;
  paid_at: string | null;
  paid_by: string | null;
  payment_notes: string | null;
  /** Trolley payout attempts sent from the dashboard (see lib/trolley-payouts.ts). */
  cr_hire_payouts: TrolleyPayout[] | null;
  // When the engagement is over — separate from payment_status. A hire refunded
  // short of full delivery (brand fully refunded, job written off) keeps
  // status='confirmed' forever but gets settled_at stamped, so payment_status
  // stays 'unpaid'. settled_at is the authoritative "this is done" signal (see
  // 20260807020000_cr_hires_settled_at.sql) — the review flow must treat a
  // settled hire exactly like a paid one: no more approve/reject on its videos.
  // It also opens the brand's feedback window, and closes their flag window.
  settled_at: string | null;
  // Deadline extension
  deadline_extension_requested_at: string | null;
  deadline_extension_days_requested: number | null;
  deadline_extension_reason: string | null;
  deadline_extension_rejected_at: string | null;
  deadline_extension_rejected_by: string | null;
  deadline_extension_reject_reason: string | null;
  original_deadline_at: string | null;
  deadline_extended_at: string | null;
  deadline_extension_days_granted: number | null;
  extension_granted_by: string | null;
  // Which path produced the CURRENT extension — 'creator_request' (grant
  // extension, answering a pending ask) or 'manual' (brand/admin extended on
  // their own call, no request involved). Null on hires extended before this
  // column existed. See 20260922000001_hire_manual_deadline_extension.sql.
  deadline_extension_source: 'creator_request' | 'manual' | null;
  // Required note the admin leaves when extending manually — the creator has
  // no request of their own to point back to, so this is shown on their hire
  // card as the explanation. Null for creator_request extensions.
  deadline_manual_extension_note: string | null;
  // Brand-side money — how the brand funded this hire and what has been
  // refunded to their wallet. Separate from payment_* above (the creator payout).
  brand_rate_cents: number;
  whop_charge_status: string | null;
  credit_applied_cents: number | null;
  card_charged_cents: number | null;
  funding_source: 'credit' | 'card' | 'mixed' | null;
  refunded_credit_cents: number | null;
  refunded_at: string | null;
  refunded_by: string | null;
  refund_reason: string | null;
  roster_creators: { name: string | null; tiktok_username: string | null; email: string | null } | null;
  cr_campaigns: {
    id: string; name: string | null; product_name: string | null; product_id: string | null;
    // Multi-product campaigns — drives whether the "Videos by product"
    // breakdown is worth showing at all, and lets the bundle be named as the
    // bundle rather than looking like just another product.
    is_multi_product: boolean | null;
    bundle_product_id: string | null;
    bundle_product_name: string | null;
    // Drives the reject-video dialog's copy — a remake job can be rejected for
    // drifting from its reference video, a regular one can't. See
    // lib/rejectReasons.ts.
    is_viral_remake: boolean | null;
    // The video(s)-to-remake, reused from a regular campaign's "style
    // inspiration" field. Only meaningful when is_viral_remake is true.
    brief_video_urls: { url: string; name?: string | null; description?: string | null }[] | null;
  } | null;
};

type HireEvent = {
  id: string;
  event_type: string;
  occurred_at: string;
  metadata: Record<string, any> | null;
};

// One round of a brand⇄creator rate negotiation on this hire. Read-only here —
// admin isn't a party to the negotiation, this is visibility into it, same
// spirit as EventTimeline below. See creator-app/supabase/migrations/
// 20260825010000_cr_counter_offers.sql and Brand-Dashboard/worker/src/
// counterOffers.ts for the system this surfaces.
type CounterOfferAdminRow = {
  id: string;
  sender: 'brand' | 'creator';
  round: number;
  package_size: number;
  creator_rate_cents: number;
  brand_rate_cents: number;
  with_rights: boolean;
  note: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'countered' | 'withdrawn' | 'expired';
  decline_reason: string | null;
  created_at: string;
};

const HIRE_EVENT_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  invited:        { label: 'Invite sent',      icon: Send,           color: 'text-blue-600 bg-blue-100 border-blue-200' },
  waitlisted:     { label: 'Waitlisted',        icon: Hourglass,      color: 'text-amber-600 bg-amber-100 border-amber-200' },
  confirmed:      { label: 'Accepted',          icon: CheckCircle2,   color: 'text-emerald-600 bg-emerald-100 border-emerald-200' },
  declined:       { label: 'Declined',          icon: XCircle,        color: 'text-red-600 bg-red-100 border-red-200' },
  expired_offer:  { label: 'Expired',           icon: Clock,          color: 'text-slate-600 bg-slate-100 border-slate-200' },
  completed:      { label: 'Completed',         icon: ClipboardCheck, color: 'text-emerald-700 bg-emerald-100 border-emerald-200' },
  failed:         { label: 'Failed',            icon: AlertTriangle,  color: 'text-orange-600 bg-orange-100 border-orange-200' },
  cancelled:      { label: 'Cancelled',         icon: Ban,            color: 'text-red-600 bg-red-100 border-red-200' },
};

function hireEventMeta(eventType: string) {
  return HIRE_EVENT_META[eventType] ?? { label: eventType, icon: History, color: 'text-muted-foreground bg-muted border-border' };
}

function EventTimeline({ events }: { events: HireEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No events recorded yet.</p>;
  }
  return (
    <div className="space-y-0">
      {events.map((e, i) => {
        const meta = hireEventMeta(e.event_type);
        const Icon = meta.icon;
        return (
          <div key={e.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${meta.color}`}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              {i < events.length - 1 && <span className="w-px flex-1 bg-border my-0.5" />}
            </div>
            <div className="pb-4 min-w-0">
              <p className="text-sm font-medium">{meta.label}</p>
              <p className="text-xs text-muted-foreground">{format(new Date(e.occurred_at), 'MMM d, yyyy · h:mm a')}</p>
              {e.metadata?.decline_reason && (
                <p className="text-xs text-muted-foreground mt-1 italic">"{e.metadata.decline_reason}"</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Read-only chain view — same shape/spirit as EventTimeline, but a
// negotiation is a back-and-forth (sender matters) rather than a one-way
// history, so each row names who sent it and what happened to it.
function NegotiationTimeline({ counters }: { counters: CounterOfferAdminRow[] }) {
  if (counters.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No counter offers on this hire.</p>;
  }
  const STATUS_META: Record<CounterOfferAdminRow['status'], { label: string; color: string }> = {
    pending:   { label: 'Awaiting response', color: 'text-blue-600 bg-blue-100 border-blue-200' },
    accepted:  { label: 'Accepted',           color: 'text-emerald-600 bg-emerald-100 border-emerald-200' },
    declined:  { label: 'Declined',           color: 'text-red-600 bg-red-100 border-red-200' },
    countered: { label: 'Superseded',         color: 'text-slate-600 bg-slate-100 border-slate-200' },
    withdrawn: { label: 'Withdrawn',          color: 'text-slate-600 bg-slate-100 border-slate-200' },
    expired:   { label: 'Expired',            color: 'text-slate-600 bg-slate-100 border-slate-200' },
  };
  return (
    <div className="space-y-0">
      {counters.map((c, i) => {
        const meta = STATUS_META[c.status];
        return (
          <div key={c.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${meta.color}`}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
              </span>
              {i < counters.length - 1 && <span className="w-px flex-1 bg-border my-0.5" />}
            </div>
            <div className="pb-4 min-w-0">
              <p className="text-sm font-medium">
                Round {c.round} · {c.sender === 'brand' ? 'Brand' : 'Creator'} countered with{' '}
                {fmtCents(c.brand_rate_cents)}
                <span className="text-muted-foreground font-normal"> ({c.package_size} videos{c.with_rights ? ', w/ rights' : ''})</span>
              </p>
              <p className="text-xs text-muted-foreground">{format(new Date(c.created_at), 'MMM d, yyyy · h:mm a')}</p>
              <Badge className={`mt-1.5 ${meta.color}`}>{meta.label}</Badge>
              {c.decline_reason && (
                <p className="text-xs text-muted-foreground mt-1 italic">"{c.decline_reason}"</p>
              )}
              {c.note && (
                <p className="text-xs text-muted-foreground mt-1">"{c.note}"</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fmt(n: number | null | undefined, decimals = 0) {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtCents(n: number | null | undefined) {
  if (n == null) return '—';
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color ?? ''}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

/**
 * Per-product breakdown of a hire's videos — "how many of these videos did the
 * creator actually make for each product?"
 *
 * Only meaningful on a multi-product campaign. Before those existed a hire had
 * exactly one product, so every video trivially belonged to it and the question
 * couldn't be asked; now a 10-video hire can be 10 videos on one product, or 2
 * each across 5, and the difference is invisible in the table (you'd have to
 * read the Submitted Product column row by row, across pages).
 *
 * Driven off the campaign's OWN product list, not off the submissions, so a
 * product with zero videos still gets a row. That's the half a
 * count-the-submissions approach can never show, and it's usually the one ops
 * actually wants: which product is being ignored. Anything tagged that isn't on
 * the campaign (including the bundle listing itself) is collected into an
 * "Other" group rather than dropped, so the counts always add up to the total.
 */

/** Product names are brand-entered and unbounded — capped at a fixed length
 *  wherever one sits next to a count or inside a fixed-width table cell, so a
 *  long name can never push the layout around. The full name is always one
 *  hover away via a Tooltip (Radix — not the native `title` attribute, which
 *  is unstyled, slow to appear, and unreadable against a dark row tint). */
const PRODUCT_NAME_MAX = 14;
function truncateProductName(name: string, max: number = PRODUCT_NAME_MAX): string {
  return name.length > max ? `${name.slice(0, max).trimEnd()}…` : name;
}

function VideosByProductCard({ products, videos, bundleProductId, bundleProductName }: {
  products: CampaignProductRow[];
  videos: VideoStat[];
  bundleProductId: string | null;
  bundleProductName: string | null;
}) {
  // A video's product is the one it MATCHED where we have it, else whatever was
  // tagged — same precedence (and same legacy-row fallback) as the Submitted
  // Product column in the table below.
  const keyOf = (v: VideoStat) =>
    (v.matched_product_id ?? v.submitted_product_id ?? '')?.toString().trim();

  const counted = videos.filter(v => keyOf(v));
  const countFor = (productId: string | null) =>
    productId ? counted.filter(v => keyOf(v) === productId.trim()).length : 0;

  const rows = products.map((p, i) => ({
    key: p.id,
    name: p.product_name || `Product ${i + 1}`,
    productId: p.product_id,
    image: p.product_image_urls?.[0] ?? null,
    count: countFor(p.product_id),
    isBundle: false,
  }));

  // The bundle is a real listing a creator can tag (it's the one their sample
  // request was approved against), so it earns its own row — labelled as the
  // bundle, not silently lumped into "Other".
  if (bundleProductId) {
    rows.push({
      key: 'bundle',
      name: bundleProductName || 'Bundle',
      productId: bundleProductId,
      image: null,
      count: countFor(bundleProductId),
      isBundle: true,
    });
  }

  const knownIds = new Set(rows.map(r => r.productId?.trim()).filter(Boolean));
  const otherCount = counted.filter(v => !knownIds.has(keyOf(v))).length;
  const untagged = videos.length - counted.length;

  const covered = rows.filter(r => !r.isBundle && r.count > 0).length;
  const productCount = rows.filter(r => !r.isBundle).length;
  // Highest count first — the question this card answers is "which products
  // got covered and which didn't", and that reads faster sorted than in
  // whatever order the brand happened to list them in.
  const sorted = [...rows].sort((a, b) => b.count - a.count);
  const topCount = sorted[0]?.count ?? 0;

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b px-5 py-3">
        <div className="min-w-0">
          <h2 className="font-semibold">Videos by Product</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            How this hire&apos;s {videos.length} video{videos.length !== 1 ? 's' : ''} split across the campaign&apos;s products
          </p>
        </div>
        {/* <span className="ml-auto shrink-0 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200">
          {covered}/{productCount} products covered
        </span> */}
      </div>

      {/* A tile grid, not a bar chart — a bar reads as PROGRESS toward a
          target (a "0/5" tile next to a nearly-full one looks like something
          is missing or behind), but there is no target here: a creator is
          free to put every video on one product, and that is a perfectly
          complete hire. Each count is just a fact, so it's shown as one —
          a plain number, differentiated by size/weight and a quiet fill tint
          on the leader, never by a track-and-fill meter. */}
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4">
        {sorted.map(r => {
          const isTop = r.count > 0 && r.count === topCount;
          return (
            <div
              key={r.key}
              className={`relative overflow-hidden rounded-xl border p-3 transition-colors ${
                r.count === 0
                  ? 'border-dashed bg-muted/20'
                  : isTop
                    ? 'border-indigo-200 bg-gradient-to-br from-indigo-50 to-white shadow-sm'
                    : 'border-indigo-100 bg-indigo-50/30'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border bg-white">
                  {r.image
                    ? <img src={r.image} alt="" className="h-full w-full object-cover" />
                    : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Package className="h-4 w-4 text-muted-foreground/40" />
                      </div>
                    )}
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="cursor-default truncate text-[13px] font-semibold leading-tight">
                          {truncateProductName(r.name)}
                        </p>
                      </TooltipTrigger>
                      {r.name.length > PRODUCT_NAME_MAX && (
                        <TooltipContent side="top" className="text-xs">{r.name}</TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {r.isBundle && (
                    <span className="mt-1 inline-block rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-indigo-700">
                      Bundle
                    </span>
                  )}
                </div>
                {isTop && (
                  <span className="shrink-0 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                    Most
                  </span>
                )}
              </div>

              <div className="mt-2.5 flex items-baseline gap-1">
                <span className={`text-2xl font-bold leading-none ${r.count === 0 ? 'text-muted-foreground/35' : 'text-foreground'}`}>
                  {r.count}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {r.count === 1 ? 'video' : 'videos'}
                </span>
              </div>
            </div>
          );
        })}

        {/* Tagged something that isn't on this campaign at all — worth surfacing
            rather than hiding, since it's exactly the case the Not Matched stat
            is counting. Kept in the same grid (not a separate banner) so it
            reads as one more fact about the split, not a warning interrupting it. */}
        {otherCount > 0 && (
          <div className="relative overflow-hidden rounded-xl border border-red-200 bg-red-50/50 p-3">
            <div className="flex items-start gap-2.5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-200 bg-white">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-[13px] font-semibold leading-tight text-red-900">Other</p>
                <p className="mt-0.5 text-[10.5px] leading-snug text-red-700/80">Not on this campaign</p>
              </div>
            </div>
            <div className="mt-2.5 flex items-baseline gap-1">
              <span className="text-2xl font-bold leading-none text-red-600">{otherCount}</span>
              <span className="text-[11px] text-red-700/70">{otherCount === 1 ? 'video' : 'videos'}</span>
            </div>
          </div>
        )}
      </div>

      {untagged > 0 && (
        <p className="border-t px-5 py-2.5 text-xs text-muted-foreground">
          {untagged} video{untagged !== 1 ? 's' : ''} with no product recorded — submitted before per-video product tracking, or TikTok reported none.
        </p>
      )}
    </div>
  );
}

function HireStatusBadge({ status }: { status: string }) {
  if (status === 'confirmed')         return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Confirmed</Badge>;
  if (status === 'completed')         return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Completed</Badge>;
  if (status === 'partial_completed') return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Partial Completed</Badge>;
  if (status === 'failed')            return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Failed</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function SortHead({ field, label, active, dir, onSort, align = '' }: {
  field: SortField; label: string; active: boolean; dir: SortDir;
  onSort: (f: SortField) => void; align?: string;
}) {
  return (
    <TableHead
      className={`cursor-pointer select-none whitespace-nowrap ${align}`}
      onClick={() => onSort(field)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'text-right' ? 'justify-end w-full' : ''}`}>
        {label}
        {active
          ? dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
          : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />}
      </span>
    </TableHead>
  );
}

function deadlineStatus(deadline_at: string | null): { label: string; className: string } {
  if (!deadline_at) return { label: '—', className: 'text-muted-foreground' }
  const days = Math.ceil((new Date(deadline_at).getTime() - Date.now()) / 86400000)
  if (days < 0) return { label: `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`, className: 'text-red-500 font-semibold' }
  if (days === 0) return { label: 'Due today', className: 'text-orange-500 font-semibold' }
  if (days <= 3) return { label: `${days} day${days !== 1 ? 's' : ''} left`, className: 'text-red-500 font-semibold' }
  if (days <= 7) return { label: `${days} days left`, className: 'text-amber-500 font-semibold' }
  return { label: `${days} days left`, className: 'text-emerald-600 font-semibold' }
}

/**
 * Brand-side money for this hire: what they paid, how (credit/card/mixed), what
 * has come back, and the refund action when the hire missed its deadline short.
 *
 * The refund credits the brand's WALLET — no card refund. Eligibility shown here
 * mirrors the DB rules (see lib/refunds.ts); the database re-checks all of them,
 * so the worst a stale UI can do is show a button that comes back with the
 * specific reason it was rejected.
 */
function RefundPanel({ hire, deliveredVideos, onRefunded }: {
  hire: Hire; deliveredVideos: number; onRefunded: () => void;
}) {
  const state = refundState(hire, deliveredVideos)
  const [open, setOpen] = useState(false)
  // Video-driven, same as the Creator Payments dialog: ops picks how many
  // undelivered videos to refund and the amount follows from the per-video price
  // the brand paid. No free-form dollar entry on the one action that mints money.
  const [mode, setMode] = useState<'full' | 'partial'>('full')
  const [videos, setVideos] = useState(0)
  const [reason, setReason] = useState('')
  const [key, setKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Nothing was ever collected from the brand — there's no money story to tell.
  if (state.collectedCents <= 0 && !state.refundedCents) return null

  // Full still means "everything left on the hire", which can exceed the
  // undelivered entitlement — that's the goodwill refund, and it stays available
  // as an explicit choice. Partial is bounded by the unsettled entitlement.
  const amountCents = mode === 'full' ? state.refundableCents : partialRefundCents(state, videos)
  const videosKeptPaid = mode === 'full' ? 0 : hire.videos_required - videos
  const canSubmit = amountCents > 0
    && amountCents <= state.refundableCents
    && (mode === 'full' || videos > 0)
    && !loading

  function openForm() {
    // Nothing delivered → full is the only option. Otherwise default to
    // refunding exactly the gap.
    setMode(state.fullOnly ? 'full' : 'partial')
    // Videos still UNREFUNDED, not every undelivered one — on a second refund the
    // ones already settled must not be offered again.
    setVideos(state.refundableVideos)
    setReason('')
    // One key per opened form: retries of THIS refund are a no-op, a later
    // deliberate top-up gets its own.
    setKey(`hire_refund:${hire.id}:${crypto.randomUUID()}`)
    setError(null)
    setOpen(true)
  }

  async function submit() {
    setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const adminEmail = session?.user?.email ?? ''
      if (!adminEmail) throw new Error('Could not determine your admin email — sign out and back in, then retry.')
      await refundHireToCredit(hire.id, adminEmail, {
        amount_cents: amountCents,
        reason: reason.trim() || undefined,
        idempotency_key: key,
      })
      setOpen(false)
      onRefunded()
    } catch (e: any) {
      setError(e.message ?? 'Refund failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Wallet className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Brand Payment & Refund</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Brand Paid</p>
          <p className="font-medium">{usd(state.collectedCents)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Funded By</p>
          <p className="font-medium">{fundingLabel(hire) ?? '—'}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Refunded to Wallet</p>
          <p className={`font-medium ${state.refundedCents > 0 ? 'text-violet-700' : ''}`}>
            {state.refundedCents > 0 ? usd(state.refundedCents) : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Still Refundable</p>
          {/* The unsettled ENTITLEMENT, not collected-minus-refunded. Those differ
              once a refund has landed, and the difference is the price of videos
              the brand actually received — showing that as "Still Refundable" is
              what made a settled hire look like it still owed money. */}
          <p className="font-medium">{usd(state.remainingOwedCents)}</p>
          {state.refundableCents > state.remainingOwedCents && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {usd(state.refundableCents)} on the hire, held against{' '}
              {state.deliveredVideos} delivered video{state.deliveredVideos === 1 ? '' : 's'}
            </p>
          )}
        </div>
      </div>

      {state.refundedCents > 0 && (
        <div className="rounded-md border border-violet-200 bg-violet-50 p-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-violet-800">
              {state.fullyRefunded ? 'Fully refunded as wallet credit' : 'Partially refunded as wallet credit'}
            </p>
            {/* Money that moved with nobody deciding it needs to say so at a
                glance. isSystemActor reads the 'system:' prefix that automated
                callers pass as p_created_by — no extra column, and queryable as
                refunded_by LIKE 'system:%'. */}
            {isSystemActor(hire.refunded_by) && (
              <Badge className="bg-slate-200 text-slate-700 border border-slate-300 text-[10px] px-1.5 py-0">
                Automatic
              </Badge>
            )}
          </div>
          <p className="text-xs text-violet-700">
            {usd(state.refundedCents)} credited
            {hire.refunded_by && <> by {refundActorLabel(hire.refunded_by)}</>}
            {hire.refunded_at && <> on {format(new Date(hire.refunded_at), 'MMM d, yyyy')}</>}.
            The brand&apos;s card was not refunded.
          </p>
          {hire.refund_reason && (
            <p className="text-xs text-violet-900 leading-relaxed border-l-2 border-violet-400 pl-2 mt-1">
              {hire.refund_reason}
            </p>
          )}
        </div>
      )}

      {!open ? (
        state.eligible ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={openForm}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Refund brand as credit
            </Button>
            <span className="text-xs text-muted-foreground">
              {deliveredVideos}/{hire.videos_required} delivered · deadline passed
            </span>
          </div>
        ) : state.inGracePeriod ? (
          // A temporary block, unlike every other blockedReason — the creator's
          // 48h post-deadline window is still open, and the refund unlocks by
          // itself when it closes. Styled as a wait (amber, with the unlock time)
          // rather than muted grey text ops might read as "never".
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
            <p className="text-sm font-semibold text-amber-900">
              Refund opens in {graceHoursRemaining(hire.deadline_at)}h
            </p>
            <p className="text-xs text-amber-800 leading-relaxed">
              The deadline has passed, but the creator still has {SUBMISSION_GRACE_PERIOD_HOURS}h from it to submit
              the remaining {state.undeliveredVideos} video{state.undeliveredVideos === 1 ? '' : 's'} or request an
              extension. Refunding now could hand the brand its money back for videos that then land — or defund a
              hire that gets extended.
              {state.graceEndsAt && <> Opens {format(state.graceEndsAt, 'MMM d, h:mm a')}.</>}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Refund unavailable — {state.blockedReason}</p>
        )
      ) : (
        <div className="rounded-md border p-3 space-y-3">
          {/* Full vs partial. Hidden when nothing was delivered — there's no
              video to keep paying for, so full is the only coherent choice. */}
          {state.fullOnly ? (
            <p className="text-xs text-muted-foreground">
              Nothing was delivered, so this is a <strong>full refund</strong> of
              {usd(state.refundableCents)}.
            </p>
          ) : (
            <div className="flex gap-2">
              {([
                { key: 'partial' as const, label: 'Partial' },
                { key: 'full'    as const, label: 'Full' },
              ]).map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setMode(opt.key)}
                  className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                    mode === opt.key ? 'border-violet-400 bg-violet-50 text-violet-800' : 'hover:bg-muted/50'
                  }`}
                >
                  {opt.label} refund
                </button>
              ))}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {mode === 'partial' && !state.fullOnly && (
              <div className="space-y-1">
                <label htmlFor="rp-videos" className="text-xs text-muted-foreground">Videos to refund</label>
                <Input
                  id="rp-videos"
                  type="number" min={1} max={state.refundableVideos}
                  value={videos}
                  onChange={e => {
                    const n = parseInt(e.target.value, 10)
                    setVideos(Number.isFinite(n) ? Math.max(0, Math.min(n, state.refundableVideos)) : 0)
                  }}
                  className="h-8"
                />
                <p className="text-[11px] text-muted-foreground">
                  {state.refundableVideos} still refundable
                  {state.refundableVideos !== state.undeliveredVideos &&
                    ` of ${state.undeliveredVideos} undelivered`}
                  {' · '}{usd(state.perVideoCents)} per video
                </p>
              </div>
            )}
            <div className="space-y-1">
              <label htmlFor="rp-reason" className="text-xs text-muted-foreground">Reason (shown to the brand)</label>
              <Input
                id="rp-reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Deadline passed, videos not delivered"
                className="h-8"
              />
            </div>
          </div>

          {/* Which videos the creator keeps being paid for. The full table is
              already on this page below, so this just states the count and
              points at it rather than duplicating the list. */}
          {mode === 'partial' && !state.fullOnly && (
            <p className="text-xs text-muted-foreground">
              Still paying for <strong>{videosKeptPaid}</strong> delivered
              video{videosKeptPaid === 1 ? '' : 's'} — listed in the submissions table below.
            </p>
          )}

          <div className="rounded-md bg-violet-50 border border-violet-200 px-3 py-2 flex items-baseline justify-between">
            <span className="text-xs font-semibold text-violet-900">Credit to wallet</span>
            <span className="text-sm font-bold tabular-nums text-violet-900">{usd(amountCents)}</span>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" disabled={!canSubmit} onClick={submit}>
              {loading ? 'Refunding…' : `Credit ${usd(amountCents)} to wallet`}
            </Button>
            <Button size="sm" variant="ghost" disabled={loading} onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Creator payout — the money going OUT to the creator, as opposed to
 * RefundPanel's money going back to the brand.
 *
 * Two shapes, both from payoutState(): a full payout on a completed delivery, and
 * a partial one paying only for the videos that arrived. The partial payout is
 * gated on the brand having been refunded first (see lib/payouts.ts for why), so
 * when it's blocked this panel sits directly under the Refund button that unblocks
 * it — the blocked reason names that as the next step.
 *
 * The write is recordHirePayment(), shared with the Creator Payments table so both
 * entry points produce an identical row and audit entry.
 */
function PayoutPanel({ hire, deliveredVideos, pendingVideos = 0, onPaid }: {
  hire: Hire; deliveredVideos: number; pendingVideos?: number; onPaid: () => void;
}) {
  const state = payoutState(hire, deliveredVideos, pendingVideos)
  const trolley = latestTrolleyPayout(hire.cr_hire_payouts)
  const [open, setOpen] = useState(false)
  const [method, setMethod] = useState<PayMethod>('trolley')
  const [txId, setTxId] = useState('')
  const [amount, setAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const amountCents = Math.round(Number(amount) * 100)
  const overEarned = amountCents > state.earnedCents
  const canSubmit = txId.trim().length > 0 && amountCents > 0 && !loading

  // A payout can only exist on a hire the creator actually accepted. On an
  // invite that was declined or expired there's no payout story to tell, and
  // "Payout unavailable" would be noise rather than information. Renders anyway
  // if something was already paid — that's a fact worth showing on any hire.
  const payoutRelevant = ['confirmed', 'completed', 'partial_completed', 'failed'].includes(hire.status)
  if (!state.alreadyPaid && !payoutRelevant) return null

  function openForm() {
    setTxId('')
    // Seed what was EARNED, never the hire's full rate — on a partial payout the
    // rate is both the wrong number and the plausible-looking one.
    setAmount(String((hire.payment_amount_cents || state.earnedCents) / 100))
    setNotes('')
    setError(null)
    setMethod('trolley')
    setOpen(true)
  }

  async function submit() {
    setLoading(true)
    setError(null)
    try {
      await recordHirePayment(hire, {
        transactionId: txId,
        amountCents,
        notes,
        deliveredVideos,
      })
      setOpen(false)
      onPaid()
    } catch (e: any) {
      setError(e.message ?? 'Failed to record payment')
    } finally {
      setLoading(false)
    }
  }

  return (
    // The separator belongs to the panel, not the page: this component can bail
    // out above, and a divider left behind by a panel that isn't there reads as a
    // rendering bug.
    <div className="space-y-3">
      <Separator />
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Creator Payout</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Creator Rate</p>
          <p className="font-medium">{usd(state.fullRateCents)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Delivered</p>
          <p className="font-medium">{state.deliveredVideos} / {state.requiredVideos}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Earned</p>
          <p className="font-medium">{usd(state.earnedCents)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Withheld</p>
          <p className={`font-medium ${state.withheldCents > 0 ? 'text-amber-700' : ''}`}>
            {state.withheldCents > 0 ? usd(state.withheldCents) : '—'}
          </p>
        </div>
      </div>

      {trolley && <TrolleyPayoutStatusCard payout={trolley} onChanged={onPaid} />}

      {state.alreadyPaid ? (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-1">
          <p className="text-sm font-semibold text-green-800">
            {/* A payout that settled for less than the rate has to say so — it
                otherwise reads as fully settled and the withheld amount is
                invisible on a page that shows both numbers. */}
            {(hire.payment_amount_cents ?? 0) > 0 && hire.payment_amount_cents < state.fullRateCents
              ? `Paid ${usd(hire.payment_amount_cents)} of ${usd(state.fullRateCents)} — partial payout`
              : `Paid ${usd(hire.payment_amount_cents ?? state.fullRateCents)}`}
          </p>
          <p className="text-xs text-green-700">
            {hire.paid_by && <>by {hire.paid_by} </>}
            {hire.paid_at && <>on {format(new Date(hire.paid_at), 'MMM d, yyyy')} </>}
            {hire.payment_transaction_id && <>· tx {hire.payment_transaction_id}</>}
          </p>
          {hire.payment_notes && (
            <p className="text-xs text-green-900 leading-relaxed border-l-2 border-green-400 pl-2 mt-1">
              {hire.payment_notes}
            </p>
          )}
        </div>
      ) : !open ? (
        state.eligible ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={state.mode === 'partial' ? 'outline' : 'default'}
              className={state.mode === 'partial' ? 'border-amber-300 text-amber-800 hover:bg-amber-50' : undefined}
              onClick={openForm}
            >
              <DollarSign className="h-3.5 w-3.5 mr-1" />
              {state.mode === 'partial'
                ? `Pay ${usd(state.earnedCents)} for ${state.deliveredVideos} of ${state.requiredVideos}`
                : `Pay ${usd(state.earnedCents)}`}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Payout unavailable — {state.blockedReason}</p>
        )
      ) : (
        <div className="rounded-md border p-3 space-y-3">
          <TrolleyPayoutMethodToggle value={method} onChange={setMethod} disabled={loading} />
          {state.mode === 'partial' && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-1">
              <div className="flex justify-between gap-3">
                <span>Per video ({usd(state.fullRateCents)} ÷ {state.requiredVideos})</span>
                <span className="tabular-nums">{usd(state.perVideoCents)}</span>
              </div>
              <div className="flex justify-between gap-3 font-medium">
                <span>Earned ({state.deliveredVideos} delivered)</span>
                <span className="tabular-nums">{usd(state.earnedCents)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span>Withheld ({state.undeliveredVideos} undelivered)</span>
                <span className="tabular-nums">−{usd(state.withheldCents)}</span>
              </div>
              {/* Only a PARTIAL refund can reach this form — a full refund writes the
                  job off and payoutState blocks the payout, so the old "comes out of
                  margin" warning is no longer reachable. */}
              {state.brandCollectedCents > 0 && (
                <p className="border-t border-amber-200 pt-1.5 text-amber-700">
                  Brand refunded {usd(state.brandRefundedCents)} of {usd(state.brandCollectedCents)} — still paying
                  for the {state.deliveredVideos} video{state.deliveredVideos === 1 ? '' : 's'} that arrived.
                </p>
              )}
            </div>
          )}

          {method === 'trolley' ? (
            <TrolleyPayoutSendForm
              compact
              hireId={hire.id}
              onCancel={() => setOpen(false)}
              onSent={(sent) => {
                if (sent && (sent.status === 'submitted' || sent.status === 'processed')) setOpen(false)
                onPaid()
              }}
            />
          ) : (<>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="pp-tx" className="text-xs text-muted-foreground">Transaction ID *</label>
              <Input
                id="pp-tx"
                value={txId}
                onChange={e => setTxId(e.target.value)}
                placeholder="e.g. TXN-2026-0001"
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="pp-amount" className="text-xs text-muted-foreground">Amount ($) *</label>
              <Input
                id="pp-amount"
                type="number" min="0" step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="h-8"
              />
              {/* Editable so ops can settle bonuses and agreed adjustments here.
                  Overpaying is the only direction that costs money, so it's
                  flagged rather than blocked. */}
              {overEarned && (
                <p className="text-[11px] text-amber-700">
                  {usd(amountCents - state.earnedCents)} above the {usd(state.earnedCents)} earned
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="pp-notes" className="text-xs text-muted-foreground">Notes (internal)</label>
            <Input
              id="pp-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional — why this amount, any adjustment applied"
              className="h-8"
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" disabled={!canSubmit} onClick={submit}>
              {loading ? 'Saving…' : `Pay ${usd(amountCents > 0 ? amountCents : 0)}`}
            </Button>
            <Button size="sm" variant="ghost" disabled={loading} onClick={() => setOpen(false)}>Cancel</Button>
          </div>
          </>)}
        </div>
      )}
    </div>
  )
}

/**
 * Applies a deadline extension to a hire — the DB write shared by DeadlinePanel's
 * request-driven grant flow and RejectedVideoDeadlineBanner's proactive nudge
 * below (a rejection can leave a hire short with no pending request from the
 * creator at all). Extends from whichever is later, the current deadline or
 * right now, so a hire that's already overdue doesn't get "extended" into a
 * date that's still in the past.
 */
async function grantDeadlineExtension(hire: Hire, days: number, adminEmail: string) {
  const base = hire.deadline_at ? new Date(hire.deadline_at).getTime() : Date.now()
  const newDeadline = new Date(Math.max(base, Date.now()) + days * 86400000)
  const payload = {
    deadline_at: newDeadline.toISOString(),
    original_deadline_at: hire.original_deadline_at ?? hire.deadline_at,
    deadline_extended_at: new Date().toISOString(),
    deadline_extension_days_granted: days,
    extension_granted_by: adminEmail,
  }
  const { error } = await supabase.from('cr_hires').update(payload).eq('id', hire.id)
  if (error) throw error
  return { payload, newDeadline }
}

function DeadlinePanel({ hire, onGranted }: { hire: Hire; onGranted: () => void }) {
  const [grantDays, setGrantDays] = useState('')
  const [grantLoading, setGrantLoading] = useState(false)
  const [grantError, setGrantError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectLoading, setRejectLoading] = useState(false)
  const [rejectError, setRejectError] = useState<string | null>(null)

  // Manual extension — admin extends on their own call, no creator request
  // involved. Separate state from the grant/reject form above so the two
  // flows never bleed into each other.
  const [showManualForm, setShowManualForm] = useState(false)
  const [manualDays, setManualDays] = useState('14')
  const [manualNote, setManualNote] = useState('')
  const [manualLoading, setManualLoading] = useState(false)
  const [manualError, setManualError] = useState<string | null>(null)

  const parsedDays = parseInt(grantDays, 10)
  const validDays = !isNaN(parsedDays) && parsedDays >= 1 && parsedDays <= 90

  const newDeadlinePreview = validDays && hire.deadline_at
    ? new Date(Math.max(new Date(hire.deadline_at).getTime(), Date.now()) + parsedDays * 86400000)
    : null

  const parsedManualDays = parseInt(manualDays, 10)
  const validManualDays = !isNaN(parsedManualDays) && parsedManualDays >= 1 && parsedManualDays <= 90
  // Required — the creator has no request of their own to point back to, so
  // this note is the only explanation they see on their hire card. Same
  // 10-char floor as the brand worker's extend-deadline endpoint.
  const validManualNote = manualNote.trim().length >= 10
  const newManualDeadlinePreview = validManualDays
    ? new Date(Math.max(hire.deadline_at ? new Date(hire.deadline_at).getTime() : Date.now(), Date.now()) + parsedManualDays * 86400000)
    : null

  // A hire stays 'confirmed' after a refund (status never changes — see
  // 20260805000000_wallet_refund_keeps_hire_status.sql), so status alone
  // can't tell a refunded hire apart from a live one. Checks
  // refunded_credit_cents directly rather than only settled_at: settled_at
  // is skipped when a refund is issued early via p_allow_before_deadline,
  // but the money still moved. Mirrors hireIsRefunded in
  // Brand-Dashboard/worker/src/hiring.ts.
  const refunded = !!hire.refunded_credit_cents || !!hire.settled_at

  const handleGrant = async () => {
    if (!validDays) return
    if (refunded) { setGrantError('This hire has already been refunded — an extension can no longer be granted.'); return }
    setGrantLoading(true)
    setGrantError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const adminEmail = session?.user?.email ?? 'admin'
      const grantPayload = {
        deadline_at: newDeadlinePreview!.toISOString(),
        original_deadline_at: hire.original_deadline_at ?? hire.deadline_at,
        deadline_extended_at: new Date().toISOString(),
        deadline_extension_days_granted: parsedDays,
        extension_granted_by: adminEmail,
        deadline_extension_source: 'creator_request' as const,
        deadline_manual_extension_note: null,
      }
      const { error } = await supabase
        .from('cr_hires')
        .update(grantPayload)
        .eq('id', hire.id)
      if (error) {
        await logUpdate('cr_hires', hire.id, { deadline_at: hire.deadline_at }, grantPayload, {
          description: `Failed to grant deadline extension — +${parsedDays}d`,
          creator_id: hire.creator_id,
          campaign_id: hire.campaign_id,
          status: 'failed',
          error: error.message,
        })
        throw error
      }
      await logUpdate('cr_hires', hire.id, {
        deadline_at: hire.deadline_at,
        deadline_extension_requested_at: hire.deadline_extension_requested_at,
        deadline_extension_days_requested: hire.deadline_extension_days_requested,
      }, grantPayload, {
        description: `Admin granted deadline extension — +${parsedDays}d (new deadline: ${format(newDeadline, 'MMM d, yyyy')})`,
        creator_id: hire.creator_id,
        campaign_id: hire.campaign_id,
        days_granted: parsedDays,
      })
      setShowForm(false)
      setGrantDays('')
      onGranted()
    } catch (e: any) {
      setGrantError(e.message ?? 'Failed to grant extension')
    } finally {
      setGrantLoading(false)
    }
  }

  const handleReject = async () => {
    if (refunded) { setRejectError('This hire has already been refunded — there is nothing left to reject.'); return }
    setRejectLoading(true)
    setRejectError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const adminEmail = session?.user?.email ?? 'admin'
      const rejectPayload = {
        deadline_extension_rejected_at: new Date().toISOString(),
        deadline_extension_rejected_by: adminEmail,
        deadline_extension_reject_reason: rejectReason.trim() || null,
      }
      const { error } = await supabase
        .from('cr_hires')
        .update(rejectPayload)
        .eq('id', hire.id)
      if (error) throw error
      await logUpdate('cr_hires', hire.id, {
        deadline_extension_requested_at: hire.deadline_extension_requested_at,
        deadline_extension_days_requested: hire.deadline_extension_days_requested,
        deadline_extension_reason: hire.deadline_extension_reason,
        deadline_extension_rejected_at: null,
      }, rejectPayload, {
        description: `Admin rejected deadline extension request${rejectReason.trim() ? ` — reason: ${rejectReason.trim()}` : ''}`,
        creator_id: hire.creator_id,
        campaign_id: hire.campaign_id,
        days_requested: hire.deadline_extension_days_requested,
      })
      setShowRejectForm(false)
      setRejectReason('')
      onGranted()
    } catch (e: any) {
      setRejectError(e.message ?? 'Failed to reject extension')
    } finally {
      setRejectLoading(false)
    }
  }

  // Admin extends the deadline on their own call — no creator request
  // needed. Kept separate from handleGrant (which answers a pending
  // request) so deadline_extension_source records which path produced the
  // CURRENT extension. Refuses while a request is pending, same as the
  // brand worker's handleManualExtendDeadline — a pending ask should be
  // answered via Grant/Reject above, not silently overridden here.
  const handleManualExtend = async () => {
    if (!validManualDays || !validManualNote) return
    if (refunded) { setManualError('This hire has already been refunded or settled — an extension can no longer be granted.'); return }
    if (extensionPending) { setManualError('This creator has a pending extension request — grant or reject it above instead.'); return }
    setManualLoading(true)
    setManualError(null)
    const noteValue = manualNote.trim()
    const manualPayload = {
      deadline_at: newManualDeadlinePreview!.toISOString(),
      original_deadline_at: hire.original_deadline_at ?? hire.deadline_at,
      deadline_extended_at: new Date().toISOString(),
      deadline_extension_days_granted: parsedManualDays,
      extension_granted_by: 'admin',
      deadline_extension_source: 'manual' as const,
      deadline_manual_extension_note: noteValue,
    }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      manualPayload.extension_granted_by = session?.user?.email ?? 'admin'
      const { error } = await supabase
        .from('cr_hires')
        .update(manualPayload)
        .eq('id', hire.id)
      if (error) throw error
      await logUpdate('cr_hires', hire.id, {
        deadline_at: hire.deadline_at,
        deadline_extension_source: hire.deadline_extension_source,
      }, manualPayload, {
        status: 'success',
        description: `Admin manually extended deadline — +${parsedManualDays}d, no creator request (new deadline: ${format(newManualDeadlinePreview!, 'MMM d, yyyy')})`,
        creator_id: hire.creator_id,
        campaign_id: hire.campaign_id,
        days_granted: parsedManualDays,
        note: noteValue,
      })
      setShowManualForm(false)
      setManualDays('14')
      setManualNote('')
      onGranted()
    } catch (e: any) {
      const message = e.message ?? 'Failed to extend deadline'
      setManualError(message)
      await logUpdate('cr_hires', hire.id, {
        deadline_at: hire.deadline_at,
        deadline_extension_source: hire.deadline_extension_source,
      }, manualPayload, {
        status: 'failed',
        error: message,
        description: `Failed to manually extend deadline — +${parsedManualDays}d, no creator request`,
        creator_id: hire.creator_id,
        campaign_id: hire.campaign_id,
        days_granted: parsedManualDays,
      })
    } finally {
      setManualLoading(false)
    }
  }

  const ds = deadlineStatus(hire.deadline_at)
  const extensionPending = extensionRequestPending(hire)
  const extensionRejected = !!hire.deadline_extension_rejected_at
  const wasExtended = !!hire.deadline_extended_at

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Deadline Tracking</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {wasExtended && hire.original_deadline_at && (
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Original Deadline</p>
            <p className="font-medium line-through text-muted-foreground">
              {format(new Date(hire.original_deadline_at), 'MMM d, yyyy')}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">
            {wasExtended ? 'Extended Deadline' : 'Deadline'}
          </p>
          <p className="font-medium">
            {hire.deadline_at ? format(new Date(hire.deadline_at), 'MMM d, yyyy') : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Time Remaining</p>
          <p className={ds.className}>{ds.label}</p>
        </div>
        {/* Base window this package earns, before any extension. Makes it obvious
            whether a short deadline is by design (5-video job) or a data issue. */}
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Base Window</p>
          <p className="font-medium">
            {deadlineDaysForPackage(hire.package_size)}d
            <span className="text-muted-foreground font-normal">
              {' '}({hire.package_size} videos)
            </span>
          </p>
        </div>
        {wasExtended && (
          <>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Extended On</p>
              <p className="font-medium">
                {hire.deadline_extended_at ? format(new Date(hire.deadline_extended_at), 'MMM d, yyyy') : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Granted By</p>
              <p className="font-medium truncate">{hire.extension_granted_by ?? '—'}</p>
            </div>
            {hire.deadline_extension_days_granted && (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Days Granted</p>
                <p className="font-medium">+{hire.deadline_extension_days_granted}d</p>
              </div>
            )}
            {/* Which path produced this extension — a request the creator asked
                for, or the brand/admin extending on their own call. */}
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Source</p>
              <p className="font-medium">
                {hire.deadline_extension_source === 'manual'
                  ? <span className="text-blue-700">Manual (no request)</span>
                  : 'Creator request'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Manual extension note — shown instead of a creator's own reason when
          this extension was the admin's own call. Required at write time
          (see handleManualExtend above), so any 'manual' extension has one. */}
      {wasExtended && hire.deadline_extension_source === 'manual' && hire.deadline_manual_extension_note && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <p className="text-xs text-blue-600 font-medium mb-0.5">Admin's note — shown to the creator</p>
          <p className="text-xs text-blue-900 leading-relaxed">{hire.deadline_manual_extension_note}</p>
        </div>
      )}

      {/* Pending extension request from creator */}
      {extensionPending && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm space-y-0.5">
              <p className="font-semibold text-amber-800">Creator requested a deadline extension</p>
              <p className="text-amber-700 text-xs">
                Requested +{hire.deadline_extension_days_requested ?? '?'} days
                {hire.deadline_extension_requested_at && (
                  <> on {format(new Date(hire.deadline_extension_requested_at), 'MMM d, yyyy')}</>
                )}. Use the form below to grant an extension.
              </p>
            </div>
          </div>
          {hire.deadline_extension_reason && (
            <div className="ml-6 rounded border-l-2 border-amber-400 bg-amber-100/60 px-3 py-2">
              <p className="text-xs text-amber-600 font-medium mb-0.5">Creator's reason</p>
              <p className="text-xs text-amber-900 leading-relaxed">{hire.deadline_extension_reason}</p>
            </div>
          )}
        </div>
      )}

      {/* Rejection history */}
      {extensionRejected && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-800">Extension request was rejected</p>
              <p className="text-red-600 text-xs mt-0.5">
                By {hire.deadline_extension_rejected_by ?? 'admin'}
                {hire.deadline_extension_rejected_at && (
                  <> on {format(new Date(hire.deadline_extension_rejected_at), 'MMM d, yyyy')}</>
                )}
              </p>
              {hire.deadline_extension_reject_reason && (
                <p className="text-red-700 text-xs mt-1 italic">"{hire.deadline_extension_reject_reason}"</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refunded, so the pending request above is moot — no action left to take. */}
      {extensionPending && refunded && (
        <div className="rounded-md border border-muted bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            This hire has already been refunded, so this request can no longer be granted or rejected.
          </p>
        </div>
      )}

      {/* Grant / Reject actions — only shown when request is pending and the hire hasn't been refunded */}
      {hire.status === 'confirmed' && extensionPending && !refunded && (
        <div className="space-y-2">
          {/* Grant form */}
          {showForm ? (
            <div className="rounded-md border bg-muted/30 p-3 space-y-3">
              <p className="text-sm font-semibold">Grant Deadline Extension</p>
              {grantError && <p className="text-xs text-red-500">{grantError}</p>}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    placeholder={String(hire.deadline_extension_days_requested ?? 14)}
                    value={grantDays}
                    onChange={e => setGrantDays(e.target.value)}
                    className="w-24 h-8 text-sm"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
                {newDeadlinePreview && (
                  <span className="text-xs text-emerald-600 font-medium">
                    → Due {format(newDeadlinePreview, 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleGrant} disabled={!validDays || grantLoading}>
                  {grantLoading ? 'Granting…' : 'Grant extension'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setGrantDays(''); setGrantError(null) }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : showRejectForm ? (
            <div className="rounded-md border border-red-200 bg-red-50/50 p-3 space-y-3">
              <p className="text-sm font-semibold text-red-800">Reject Extension Request</p>
              {rejectError && <p className="text-xs text-red-500">{rejectError}</p>}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Reason <span className="text-muted-foreground/60">(optional)</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Let the creator know why…"
                  maxLength={300}
                  rows={2}
                  className="w-full rounded border text-sm px-3 py-2 resize-none bg-white"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="destructive" onClick={handleReject} disabled={rejectLoading}>
                  {rejectLoading ? 'Rejecting…' : 'Confirm rejection'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowRejectForm(false); setRejectReason(''); setRejectError(null) }}>
                  Back
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setGrantDays(String(hire.deadline_extension_days_requested ?? 14))
                  setShowForm(true)
                  setGrantError(null)
                }}
              >
                <CalendarClock className="h-3.5 w-3.5 mr-1" />
                Grant extension
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => { setShowRejectForm(true); setRejectError(null) }}
              >
                Reject request
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Manual extension — admin extends on their own call, no creator
          request involved. Deliberately a separate, visually distinct block
          from the Grant/Reject actions above (neutral border, not amber) so
          it never reads as answering a request. Available any time the hire
          is still open (confirmed, not refunded/settled); hidden while a
          creator request is pending — that gets resolved above instead. */}
      {hire.status === 'confirmed' && !refunded && (
        <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-blue-600" />
            <p className="text-sm font-semibold text-blue-900">Extend Deadline — No Request</p>
          </div>
          {extensionPending ? (
            <p className="text-xs text-blue-700">
              This creator has a pending extension request above — grant or reject it before extending manually.
            </p>
          ) : showManualForm ? (
            <div className="space-y-3">
              {manualError && <p className="text-xs text-red-500">{manualError}</p>}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={manualDays}
                    onChange={e => setManualDays(e.target.value)}
                    className="w-24 h-8 text-sm bg-white"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
                {newManualDeadlinePreview && (
                  <span className="text-xs text-emerald-600 font-medium">
                    → Due {format(newManualDeadlinePreview, 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Note <span className="text-muted-foreground/60">(required — the creator will see this)</span>
                </label>
                <textarea
                  value={manualNote}
                  onChange={e => setManualNote(e.target.value)}
                  placeholder="Why this extension — e.g. brand-side delay, product delayed in transit…"
                  maxLength={500}
                  rows={2}
                  className="w-full rounded border text-sm px-3 py-2 resize-none bg-white"
                />
                {manualNote.trim().length > 0 && !validManualNote && (
                  <p className="text-xs text-amber-600 mt-1">At least 10 characters — the creator needs a real reason.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleManualExtend} disabled={!validManualDays || !validManualNote || manualLoading}>
                  {manualLoading ? 'Extending…' : 'Extend deadline'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowManualForm(false); setManualDays('14'); setManualNote(''); setManualError(null) }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => { setShowManualForm(true); setManualError(null) }}>
              <CalendarClock className="h-3.5 w-3.5 mr-1" />
              Extend deadline
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// A rejection is only "late" — the thing this banner exists to flag — when it
// landed within this many days of the deadline (or after it, including the
// grace window). Mirrors the "2 days before" language from the original ask.
const REJECTION_CRUNCH_DAYS = 2

// After granting an extension, give it this long before the banner can fire
// again. A short extension (say +1 day) can still land inside
// REJECTION_CRUNCH_DAYS of the NEW deadline — without this, the banner that
// was just acted on reappears the instant the admin closes it, on the same
// hire, for the same rejection. 48h mirrors the post-deadline grace window
// elsewhere in this file, not a submission deadline of its own.
const EXTENSION_COOLDOWN_HOURS = 48

// Confirmation step for RejectedVideoDeadlineBanner's "Extend deadline" —
// granting one moves real money/timeline risk (a longer window before a
// refund becomes eligible, a later payout), so it gets the same
// gradient-hero AlertDialog treatment as ReviewVideoDialog's approve/reject
// confirmations below, just in its own blue tone to read as "extend", not
// "approve" or "reject".
function ExtendDeadlineDialog({ open, onOpenChange, currentDeadline, days, loading, error, onConfirm }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDeadline: string | null;
  days: number;
  loading: boolean;
  error: string | null;
  onConfirm: () => void;
}) {
  // Mirrors grantDeadlineExtension's own math (extend from whichever is
  // later, the current deadline or right now) so the preview never disagrees
  // with what the confirm button is about to write.
  //
  // Guarded on Number.isFinite(days): this component stays mounted (and keeps
  // re-rendering) even while the dialog is closed, so an emptied/invalid days
  // input upstream — parseInt('') is NaN — must not reach `new Date(NaN)`
  // here. An invalid Date is truthy, so the `newDeadline ? format(...) : '—'`
  // below would still call format() on it and throw "Invalid time value",
  // crashing the page well before the dialog could ever be opened with it.
  const newDeadline = currentDeadline && Number.isFinite(days)
    ? new Date(Math.max(new Date(currentDeadline).getTime(), Date.now()) + days * 86400000)
    : null;

  return (
    <AlertDialog open={open} onOpenChange={o => { if (!loading) onOpenChange(o); }}>
      <AlertDialogContent>
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 px-5 py-4 text-white">
          <div className="pointer-events-none absolute -left-10 -top-12 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="relative flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-4 bg-white/15 ring-white/30">
              <CalendarClock className="h-5 w-5" />
            </span>
            <div className="min-w-0 space-y-0.5">
              <AlertDialogTitle className="text-lg font-semibold leading-tight tracking-tight text-white">
                Extend this hire's deadline?
              </AlertDialogTitle>
              <p className="text-xs text-white/80">Gives the creator more time to submit a replacement video</p>
            </div>
          </div>
        </div>

        <AlertDialogDescription asChild>
          <div className="min-w-0 space-y-3 text-sm text-muted-foreground">
            <p>
              This grants <strong className="text-foreground">+{days} day{days === 1 ? '' : 's'}</strong> beyond
              the current deadline. The creator will have room to submit a replacement for the rejected video.
            </p>
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-muted-foreground/70">Current deadline</p>
                <p className="truncate text-xs font-medium text-foreground">
                  {currentDeadline ? format(new Date(currentDeadline), 'MMM d, yyyy') : '—'}
                </p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-muted-foreground/70">New deadline</p>
                <p className="truncate text-xs font-semibold text-emerald-700">
                  {newDeadline ? format(newDeadline, 'MMM d, yyyy') : '—'}
                </p>
              </div>
            </div>
          </div>
        </AlertDialogDescription>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <AlertDialogFooter>
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="bg-blue-600 text-white hover:bg-blue-700" disabled={loading} onClick={onConfirm}>
            {loading ? 'Extending…' : `Extend by ${days} day${days === 1 ? '' : 's'}`}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Rejecting a video that had already counted toward `videos_required` can
 * leave a hire short right as its deadline runs out — the creator now needs
 * to submit a replacement but may have little or no time left to do it.
 * Unlike DeadlinePanel's grant/reject controls above (which only appear once
 * the CREATOR has asked for more time), this is an admin-initiated nudge.
 *
 * Gated on the REJECTION's timing, not just today's date: a video rejected
 * two weeks before the deadline gave the creator two weeks to send a
 * replacement — if the deadline is now tight, that's on the creator, not the
 * review timing, and this banner has nothing useful to add. It should only
 * fire when the rejection itself is what erased the creator's runway — i.e.
 * it landed within REJECTION_CRUNCH_DAYS of the deadline or after it (grace
 * window included). Since a rejection can't happen in the future, that
 * condition already implies "now" is close to or past the deadline too, so
 * the messaging below can safely read off the current date once the gate
 * passes. A grant also gets EXTENSION_COOLDOWN_HOURS to take effect before
 * the banner can fire again — otherwise a short extension (e.g. +1 day) can
 * still land the NEW deadline inside REJECTION_CRUNCH_DAYS, and the banner
 * an admin just acted on reappears immediately on the same rejection. Sits
 * above the Videos table so it's the first thing an admin sees right after
 * rejecting the video that caused the shortfall. Renders nothing outside
 * that window, so it never competes with or duplicates the pending-request
 * flow in DeadlinePanel.
 */
function RejectedVideoDeadlineBanner({ hire, delivered, videosRequired, lastRejectedAt, creatorWindowClosed, onExtended }: {
  hire: Hire;
  delivered: number;
  videosRequired: number;
  lastRejectedAt: string | null;
  /** Paid out, or settled — the creator can't send a replacement either way, so
   *  there is no deadline left to extend. NOT the review lock (payoutClosed):
   *  a partially refunded hire still has videos for ops to decide on. */
  creatorWindowClosed: boolean;
  onExtended: () => void;
}) {
  const [days, setDays] = useState('3')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const stillShort = delivered < videosRequired
  // 'partial_completed' is the exact status a rejection dropping an
  // already-completed hire below videos_required lands it in (see
  // 20260716000005 + 20260727000015) — still an active hire, still eligible.
  const hireInPlay = !creatorWindowClosed && (hire.status === 'confirmed' || hire.status === 'partial_completed')

  let lateRejection = false
  if (lastRejectedAt && hire.deadline_at) {
    const deadlineMs = new Date(hire.deadline_at).getTime()
    const rejectedMs = new Date(lastRejectedAt).getTime()
    if (!isNaN(deadlineMs) && !isNaN(rejectedMs)) {
      const daysBeforeDeadline = Math.ceil((deadlineMs - rejectedMs) / 86400000)
      lateRejection = daysBeforeDeadline <= REJECTION_CRUNCH_DAYS
    }
  }

  // Was an extension granted recently enough that this banner should hold
  // off and let it take effect, instead of nagging again right away?
  const recentlyExtended = !!hire.deadline_extended_at
    && (Date.now() - new Date(hire.deadline_extended_at).getTime()) < EXTENSION_COOLDOWN_HOURS * 3600000

  if (!hireInPlay || !stillShort || !lateRejection || recentlyExtended) return null

  const daysLeft = hire.deadline_at ? Math.ceil((new Date(hire.deadline_at).getTime() - Date.now()) / 86400000) : null
  const pastGrace = isPastGracePeriod(hire.deadline_at)
  const inGrace = isWithinGracePeriod(hire.deadline_at)

  const parsedDays = parseInt(days, 10)
  const validDays = !isNaN(parsedDays) && parsedDays >= 1 && parsedDays <= 90
  const shortBy = videosRequired - delivered

  const handleExtend = async () => {
    if (!validDays) return
    setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const adminEmail = session?.user?.email ?? 'admin'
      const { payload, newDeadline } = await grantDeadlineExtension(hire, parsedDays, adminEmail)
      await logUpdate('cr_hires', hire.id, {
        deadline_at: hire.deadline_at,
      }, payload, {
        description: `Admin extended deadline by +${parsedDays}d after a rejection left the hire short (new deadline: ${format(newDeadline, 'MMM d, yyyy')})`,
        creator_id: hire.creator_id,
        campaign_id: hire.campaign_id,
        days_granted: parsedDays,
      })
      setConfirmOpen(false)
      onExtended()
    } catch (e: any) {
      setError(e.message ?? 'Failed to extend deadline')
    } finally {
      setLoading(false)
    }
  }

  const blocked = pastGrace
  const headline = blocked
    ? "Deadline has passed — the creator can no longer submit a replacement"
    : inGrace
    ? `Deadline passed — only ${graceHoursRemaining(hire.deadline_at)}h left in the grace window to submit a replacement`
    : daysLeft === 0
    ? 'Deadline is today — the creator has no time left to submit a replacement'
    : `Only ${daysLeft} day${daysLeft === 1 ? '' : 's'} left for the creator to submit a replacement`

  const tone = blocked
    ? { ring: 'ring-red-100', badgeBg: 'bg-red-100', icon: 'text-red-600', headline: 'text-red-900', body: 'text-red-700/90', chipBg: 'bg-red-100', chipText: 'text-red-700', glow: 'bg-red-200/50', border: 'border-red-200/80', wash: 'from-red-50 via-red-50/70 to-rose-50/30', button: 'bg-red-600 hover:bg-red-700' }
    : { ring: 'ring-amber-100', badgeBg: 'bg-amber-100', icon: 'text-amber-600', headline: 'text-amber-900', body: 'text-amber-700/90', chipBg: 'bg-amber-100', chipText: 'text-amber-700', glow: 'bg-amber-200/50', border: 'border-amber-200/80', wash: 'from-amber-50 via-amber-50/70 to-orange-50/30', button: 'bg-amber-600 hover:bg-amber-700' }

  return (
    <div className={`relative overflow-hidden rounded-xl border ${tone.border} p-5 shadow-sm`}>
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${tone.wash}`} />
      <div className={`pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full blur-2xl ${tone.glow}`} />
      <div className="relative flex items-start gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-4 ${tone.badgeBg} ${tone.ring}`}>
          <AlertTriangle className={`h-4 w-4 ${tone.icon}`} />
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`text-sm font-semibold leading-snug ${tone.headline}`}>{headline}</p>
            {hire.deadline_at && (
              <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone.chipBg} ${tone.chipText}`}>
                <CalendarClock className="h-3 w-3" />
                Deadline {format(new Date(hire.deadline_at), 'MMM d, yyyy')}
              </span>
            )}
          </div>
          <p className={`text-xs leading-relaxed ${tone.body}`}>
            A rejected video left this hire {shortBy} video{shortBy === 1 ? '' : 's'} short of the {videosRequired} required.
            Extend the deadline so the creator has room to deliver a replacement.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1.5">
            <Input
              type="number" min={1} max={90}
              value={days}
              onChange={e => setDays(e.target.value)}
              className="h-8 w-20 border-black/10 bg-white/80 text-sm shadow-sm"
            />
            <span className="text-xs text-muted-foreground">days</span>
            <Button
              size="sm"
              disabled={!validDays || loading}
              className={`${tone.button} shadow-sm`}
              onClick={() => { setError(null); setConfirmOpen(true) }}
            >
              {loading ? 'Extending…' : 'Extend deadline'}
            </Button>
          </div>
        </div>
      </div>

      <ExtendDeadlineDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        currentDeadline={hire.deadline_at}
        days={parsedDays}
        loading={loading}
        error={error}
        onConfirm={handleExtend}
      />
    </div>
  )
}

type ReviewMode = 'approved' | 'rejected';

// Shared confirm dialog for both video-review decisions. Approve and reject are
// the same shape - only the tone, copy and reject's required-reason rule differ,
// so one component with a mode switch keeps the two flows from drifting. Styled
// as a gradient hero card: since every video now needs an explicit decision,
// this is the gate that unlocks (or blocks) a payout, so it reads as deliberate.
// Reject mode is a drop-in replacement for the old dialog - same required
// reason, same destructive confirm, same cannot-be-undone framing.
const REVIEW_DIALOG_TONES: Record<ReviewMode, {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  description: string;
  banner: string;
  iconClass: string;
}> = {
  approved: {
    icon: CheckCircle2,
    title: 'Approve this video?',
    subtitle: 'Confirms the submission as delivered for this hire',
    description: "Approving locks this video in as delivered. It stays on the campaign and counts toward the creator's payout for this hire. This action can't be undone.",
    banner: 'from-emerald-500 via-emerald-600 to-teal-700',
    iconClass: 'bg-white/15 ring-white/30',
  },
  rejected: {
    icon: XCircle,
    title: 'Reject this video?',
    subtitle: 'Flags the submission and asks the creator for a replacement',
    description: "The creator will be notified right away and asked to submit a replacement video for this campaign. This action can't be undone.",
    banner: 'from-red-500 via-red-600 to-rose-700',
    iconClass: 'bg-white/15 ring-white/30',
  },
};

function ReviewVideoDialog({ open, onOpenChange, mode, videoUrl, loading, error, onConfirm, flagReason, isViralRemake, referenceVideos }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ReviewMode;
  videoUrl: string;
  loading: boolean;
  error: string | null;
  onConfirm: (reason: string, tags: RejectReasonTag[], otherReason?: string) => void;
  /** The brand's unanswered flag on this video, if it has one. Its presence
   *  turns Approve into a decision that needs justifying — see below. */
  flagReason?: string | null;
  /** Drives the reject step's copy — a remake job can be rejected for
   *  drifting from its reference video (concept_mismatch), a regular one
   *  can't. See lib/rejectReasons.ts. */
  isViralRemake?: boolean | null;
  /** The video(s)-to-remake, shown on the reject step so the admin is
   *  comparing against the actual concept, not memory of it. Only rendered on
   *  a remake job — cr_campaigns.brief_video_urls has no per-submission link
   *  to a specific entry, so every reference video on the campaign is shown
   *  rather than guessing which one this submission was meant to remake. */
  referenceVideos?: { url: string; name?: string | null; description?: string | null }[] | null;
}) {
  const [reason, setReason] = useState('');
  const [selectedTags, setSelectedTags] = useState<RejectReasonTag[]>([]);
  /** What "other" means — its own box, shown only while 'other' is checked.
   *  Never merged into `reason`: that note always means "what to fix" for the
   *  creator, regardless of which tags are picked. */
  const [otherReason, setOtherReason] = useState('');
  const [showInvalidReasons, setShowInvalidReasons] = useState(false);

  useEffect(() => {
    if (!open) { setReason(''); setSelectedTags([]); setOtherReason(''); setShowInvalidReasons(false); }
  }, [open]);

  const tone = REVIEW_DIALOG_TONES[mode];
  const Icon = tone.icon;
  const flagged = !!flagReason;
  const remake = isViralRemake === true;
  const otherSelected = selectedTags.includes('other');
  // Approve is normally a pure confirmation — there's nowhere to persist an
  // approval note. But approving a video the BRAND FLAGGED is overruling them,
  // and that reason has somewhere to go (brand_flag_resolution_notes) and
  // someone to read it (the brand, on their own dashboard). So a flagged
  // approve requires the same written reason a rejection does.
  const needsReason = mode === 'rejected' || flagged;
  const approveBlocked = mode === 'approved' && flagged && !reason.trim();
  // A rejection needs at least one category tag (same rule as the brand's own
  // dialog — Brand-Dashboard/worker/src/videoReview.ts refuses without one)
  // plus a note that clears the floor. 'other' additionally needs its own box
  // filled in, rather than raising the floor on `reason` itself.
  const rejectMinNote = MIN_REJECT_NOTE;
  const rejectBlocked = mode === 'rejected' && (
    selectedTags.length === 0
    || reason.trim().length < rejectMinNote
    || (otherSelected && otherReason.trim().length < MIN_REJECT_OTHER_NOTE)
  );

  function toggleTag(tag: RejectReasonTag) {
    setSelectedTags(prev => {
      const next = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      if (tag === 'other' && !next.includes('other')) setOtherReason('');
      return next;
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={o => { if (!loading) onOpenChange(o); }}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
        {/* min-w-0 applies here: AlertDialogContent is a grid, so its children
            default to min-width:auto and cannot shrink below their min-content
            width. The video URL is one long unbreakable token, and `truncate`
            sets white-space:nowrap - so without min-w-0 the boxes grow wider
            than the card's max-w and truncate never clips. All layout classes
            below are the divs' own, so nothing fights the dialog chrome. */}
        {/* Gradient hero - the modal's focal point. */}
        <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${tone.banner} px-5 py-4 text-white`}>
          <div className="pointer-events-none absolute -left-10 -top-12 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="relative flex items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-4 ${tone.iconClass}`}>
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 space-y-0.5">
              <AlertDialogTitle className="text-lg font-semibold leading-tight tracking-tight text-white">
                {tone.title}
              </AlertDialogTitle>
              <p className="text-xs text-white/80">{tone.subtitle}</p>
            </div>
          </div>
        </div>

        <AlertDialogDescription asChild>
          <div className="min-w-0 space-y-3 text-sm text-muted-foreground">
            <p>{tone.description}</p>
            {/* The video being decided on, as a clickable chip - opens the video in
                a new tab so the admin can eyeball it before confirming. Same
                truncate + min-w-0 pattern so long URLs never spill the card. */}
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-2 min-w-0 rounded-lg border bg-muted/40 px-3 py-2 transition-colors hover:border-primary/40 hover:bg-muted/70"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-colors group-hover:text-primary" />
              <span className="truncate text-xs text-muted-foreground group-hover:text-foreground" title={videoUrl}>{videoUrl}</span>
            </a>
          </div>
        </AlertDialogDescription>

        {/* What the brand wrote, verbatim — the thing this decision is
            answering. Only ever present on a flagged video. */}
        {flagged && (
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <Flag className="h-3.5 w-3.5 text-red-600" fill="currentColor" />
              The brand flagged this video
            </p>
            <blockquote className="border-l-2 border-red-300 bg-red-50/60 px-3 py-2 text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {flagReason}
            </blockquote>
          </div>
        )}

        {error && <p className="text-xs text-red-500">{error}</p>}

        {/* Reject-only additions: which job type this is, the campaign's
            reference video(s) on a remake, the brief/remake reminder, the
            reason chips, and the collapsible "not valid" list — ahead of the
            text box, mirroring Brand-Dashboard's VideoReviewModal reject step
            so brand and admin rejections carry the same structured reason. */}
        {mode === 'rejected' && (
          <div className="space-y-3">
            <span className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${
              remake ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-muted-foreground/20 bg-muted text-muted-foreground'
            }`}>
              {remake ? 'Viral video remake' : 'Regular video'}
            </span>

            {remake && referenceVideos && referenceVideos.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-2.5 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {referenceVideos.length === 1 ? 'Original video' : 'Original videos'}
                </p>
                {referenceVideos.map((rv, i) => (
                  <a
                    key={rv.url || i}
                    href={rv.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex min-w-0 items-center gap-1.5 text-xs text-foreground hover:text-primary"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/70 group-hover:text-primary" />
                    <span className="truncate">{rv.name?.trim() || 'View original'}</span>
                  </a>
                ))}
              </div>
            )}

            <p className="text-xs leading-relaxed text-muted-foreground">{rejectBriefReminder(remake)}</p>

            <div className="space-y-2">
              <p className="text-sm font-medium">
                Select a reason <span className="font-normal text-muted-foreground">(required — pick at least one)</span>
              </p>
              <div className="space-y-1.5">
                {rejectReasonTagsFor(remake).map(opt => {
                  const checked = selectedTags.includes(opt.key);
                  return (
                    <Fragment key={opt.key}>
                    <label
                      className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs leading-relaxed cursor-pointer ${
                        checked ? 'border-primary bg-primary/5' : 'border-input'
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleTag(opt.key)}
                        className="mt-0.5"
                      />
                      <span>{opt.label}</span>
                    </label>
                    {/* Tied to the 'other' checkbox specifically — what the
                        fixed list didn't cover, stored on its own column,
                        never shown to the creator. */}
                    {opt.key === 'other' && checked && (
                      <div className="ml-2 border-l-2 border-primary/20 pl-3 space-y-1">
                        <textarea
                          value={otherReason}
                          onChange={e => setOtherReason(e.target.value)}
                          rows={2}
                          maxLength={MAX_REJECT_OTHER_NOTE}
                          placeholder={otherReasonPlaceholder(remake)}
                          className="w-full rounded-md border text-xs px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-200"
                        />
                        <p className={`text-[11px] ${
                          otherReason.trim().length > 0 && otherReason.trim().length < MIN_REJECT_OTHER_NOTE
                            ? 'text-red-500'
                            : 'text-muted-foreground'
                        }`}>
                          {otherReason.trim().length > 0 && otherReason.trim().length < MIN_REJECT_OTHER_NOTE
                            ? `A few more words — at least ${MIN_REJECT_OTHER_NOTE} characters.`
                            : `${otherReason.length}/${MAX_REJECT_OTHER_NOTE} — not shown to the creator`}
                        </p>
                      </div>
                    )}
                    </Fragment>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowInvalidReasons(v => !v)}
              className="text-xs font-medium text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
            >
              What counts as a valid rejection?
            </button>
            {showInvalidReasons && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Not valid reasons: </strong>
                {rejectNotValidReasons(remake)}
              </p>
            )}

            <p className="rounded-md border bg-muted/40 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
              <strong className="text-foreground">What happens next: </strong>
              This video stops counting toward the job while it's under review. The creator can
              remake it, dispute the rejection, or drop it from the package for a reduced payout.
            </p>
          </div>
        )}

        {needsReason && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {mode === 'rejected' ? 'What needs to be fixed?' : 'Reason'}{' '}
              <span className="font-normal text-muted-foreground">
                {mode === 'rejected'
                  ? flagged ? '(shown to the creator and the brand)' : '(shown to the creator)'
                  : '(shown to the brand)'}
              </span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={mode === 'rejected'
                ? rejectNotePlaceholder(remake)
                : "e.g. Checked the brief — the video meets what this creator was briefed on, so we're paying it out."}
              rows={3}
              maxLength={2000}
              autoFocus
              className={`w-full rounded-md border text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 ${
                mode === 'rejected' ? 'focus:ring-red-200' : 'focus:ring-emerald-200'
              }`}
            />
            {mode === 'rejected' && (
              <p className={`text-xs ${
                reason.trim().length > 0 && reason.trim().length < rejectMinNote
                  ? 'text-red-500'
                  : 'text-muted-foreground'
              }`}>
                {reason.trim().length > 0 && reason.trim().length < rejectMinNote
                  ? `A few more words — at least ${rejectMinNote} characters.`
                  : `${reason.length}/2000`}
              </p>
            )}
            {mode === 'approved' && flagged && (
              <p className="text-xs text-muted-foreground">
                Approving clears the brand's flag. They see this reason on the video.
              </p>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {mode === 'approved' ? (
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={loading || approveBlocked}
              title={approveBlocked ? "Write why you're approving a video the brand flagged" : undefined}
              onClick={() => onConfirm(reason, [])}
            >
              {loading ? 'Approving…' : flagged ? 'Approve & clear flag' : 'Approve video'}
            </Button>
          ) : (
            <Button variant="destructive" disabled={loading || rejectBlocked} onClick={() => onConfirm(reason, selectedTags, otherSelected ? otherReason : undefined)}>
              {loading ? 'Rejecting…' : 'Reject and request a fix'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Approve/reject action for ANY submitted video on an unpaid, unsettled hire. We
// no longer restrict review to product-mismatched videos — every one gets an
// explicit admin decision (Approved / Rejected), so product-matched submissions
// are reviewed too rather than auto-approved. Videos already reviewed render a
// read-only badge. No decision (review_status null) still counts as DELIVERED
// elsewhere in the app (refund math, progress bars) but is NOT enough to
// complete the hire or unblock a payout — a hire only reaches 'completed' once
// enough videos are explicitly 'approved' here (see the
// notify_video_submission_approved DB trigger,
// 20260821000000_cr_hires_complete_on_approval.sql), and payoutState blocks
// money the same way. This UI is where the admin makes that call.
//
// APPROVED / REJECTED is never shown for a hire whose money is closed — the
// creator was paid out, or the job was written off by a FULL refund short of
// delivery. Both are terminal for this hire's videos: a rejection there would try
// to claw back a settled payout, and can't change any money anyway.
//
// That test is payoutClosed() (payouts.ts), NOT settled_at, which this used to
// read. settled_at is stamped by ANY refund closing a short hire, a PARTIAL one
// included — and a partial refund is exactly the case where the creator is still
// owed a payout that payoutState blocks until these videos are approved. Locking
// on it deadlocked the money: the buttons vanished, the Pay button stayed off on
// 'Review pending', and the creator could never be paid for what they delivered.
// settled_at closes the CREATOR's window (no more submissions, free to re-apply);
// it is not a statement that ops is done.
function VideoReviewCell({ video, hireId, hireLocked, campaign }: {
  video: VideoSubmission; hireId: string; hireLocked: boolean;
  /** Drives the reject dialog's copy — see ReviewVideoDialog. */
  campaign?: { is_viral_remake: boolean | null; brief_video_urls: { url: string; name?: string | null; description?: string | null }[] | null } | null;
}) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<ReviewMode>('approved');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hire-videos', hireId] });
    queryClient.invalidateQueries({ queryKey: ['hire-stats', hireId] });
    queryClient.invalidateQueries({ queryKey: ['hire-flags', hireId] });
    // A rejection can flip cr_hires.status ('completed' → 'partial_completed'
    // via the DB trigger — see 20260716000005 + 20260727000015) — without this,
    // the cached `hire` object keeps its stale status and
    // RejectedVideoDeadlineBanner's hireInPlay check stays false until a full
    // page refresh re-fetches it. Same three-way invalidation RefundPanel and
    // PayoutPanel already do on their own writes below.
    queryClient.invalidateQueries({ queryKey: ['hire-detail', hireId] });
  };

  // Flag feature retired 2026-09-28 — the brand can no longer raise a flag
  // (see Brand-Dashboard/src/pages/HiredCreators.tsx), so there is nothing
  // left here to resolve. Commented out, not deleted, in case it comes back.
  // // An unanswered brand flag on this video. Approving it then means overruling
  // // the brand, so the dialog asks for a reason and the flag is closed out with
  // // it (see resolveVideoFlag — the shared write path this and VideoFlagsCard
  // // both go through).
  // const activeFlagReason = isFlagActive(video) ? video.brand_flag_reason : null;
  const activeFlagReason: string | null = null;

  const applyReview = async (status: 'approved' | 'rejected', reason?: string, tags?: RejectReasonTag[], otherReason?: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const adminEmail = session?.user?.email ?? 'admin';
      const payload = {
        review_status: status,
        reviewed_at: new Date().toISOString(),
        reviewed_by: adminEmail,
        // Stamp the actor. A row with no source is a decision made before
        // brands could review at all — do not let new writes join them.
        review_source: 'admin' as const,
        // An approval note has nowhere to live on the review columns — when
        // the video is flagged it goes onto the flag's resolution instead
        // (below), which is also the copy the brand reads.
        reject_reason: status === 'rejected' ? (reason?.trim() || null) : null,
        reject_reason_tags: status === 'rejected' && tags?.length ? tags : null,
        // Only present alongside the 'other' tag — the CHECK constraint would
        // refuse it otherwise.
        reject_reason_other: status === 'rejected' && tags?.includes('other') ? (otherReason?.trim() || null) : null,
      };
      const { error: updErr } = await supabase
        .from('cr_video_submissions')
        .update(payload)
        .eq('id', video.id);
      if (updErr) throw updErr;
      await logUpdate('cr_video_submissions', video.id, {
        review_status: video.review_status,
        reject_reason: video.reject_reason,
      }, payload, {
        status: 'success',
        description: `Admin ${status} a video submission${status === 'rejected' && reason?.trim() ? ` — reason: ${reason.trim()}` : ''}`,
        hire_id: hireId,
        tiktok_video_url: video.tiktok_video_url,
      });

      // Flag feature retired 2026-09-28 — activeFlagReason is now always
      // null (see above), so this never runs; kept commented rather than
      // deleted in case the feature comes back.
      // // The review landed; now answer the brand's flag with the same decision.
      // // Approve → dismissed, carrying the reason the dialog required (the brand
      // // reads it). Reject → upheld, reusing the reject reason so the brand and
      // // the creator are told the same thing. Deliberately AFTER the review
      // // write and non-fatal: the review is the decision that moves the hire,
      // // and a flag left open is recoverable from the flag card, whereas a
      // // half-applied review is not.
      // if (activeFlagReason) {
      //   const res = await resolveVideoFlag(video, {
      //     resolution: status === 'approved' ? 'dismissed' : 'upheld',
      //     notes: reason?.trim() || '',
      //     hireId,
      //   });
      //   if (!res.ok && !res.stale) {
      //     setError(`Video ${status}, but the brand's flag stayed open: ${res.error || 'unknown error'}`);
      //     invalidate();
      //     return;
      //   }
      // }

      setDialogOpen(false);
      invalidate();
    } catch (e: any) {
      const message = e.message ?? `Failed to ${status} video`;
      setError(message);
      await logUpdate('cr_video_submissions', video.id, {
        review_status: video.review_status,
        reject_reason: video.reject_reason,
      }, {
        review_status: status,
        reject_reason: status === 'rejected' ? (reason?.trim() || null) : null,
      }, {
        status: 'failed',
        error: message,
        description: `Failed to ${status} a video submission${status === 'rejected' && reason?.trim() ? ` — reason: ${reason.trim()}` : ''}`,
        hire_id: hireId,
        tiktok_video_url: video.tiktok_video_url,
      });
    } finally {
      setLoading(false);
    }
  };

  // Removed from TikTok — takes priority over any prior review_status since
  // it's the more terminal state (can't earn payout regardless of match/review).
  if (video.deleted) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
        <XCircle className="h-3 w-3" />
        Deleted from TikTok
      </span>
    );
  }

  if (video.review_status === 'approved') {
    // 'auto' means the 7-day window closed with nobody deciding, so this
    // approval is an absence of objection, not a judgement — and it may sit on
    // a product-unmatched video. Amber, and said out loud, because an admin
    // reading this row is usually asking whether a human cleared it.
    const auto = video.review_source === 'auto';
    return (
      // The thumb sits UNDER the decision, never in place of it: a brand that
      // disliked a video still approved it and still paid for it, and a lone
      // "Disliked" on this row would read as the opposite.
      <div className="flex flex-col items-start gap-1">
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${auto ? 'text-amber-700' : 'text-emerald-700'}`}>
          <CheckCircle2 className="h-3 w-3" />
          {auto
            ? 'Auto-approved · no one reviewed it'
            : `Approved${video.review_source === 'brand' ? ' by the brand' : ''}${video.reviewed_by ? ` · ${video.reviewed_by}` : ''}`}
        </span>
        {/* Null unless the brand itself decided it with a thumb. */}
        <BrandReactionBadge video={video} />
      </div>
    );
  }

  if (video.review_status === 'rejected') {
    // The trigger only ever showed reject_reason (the "what to fix" note) —
    // the category tags the brand/admin picked, and what "other" meant when
    // picked, were written to the DB but never surfaced back on this row.
    // Both now show in the tooltip, ahead of the note, same order the reject
    // dialog collects them in.
    const tagLabels = rejectReasonLabels(video.reject_reason_tags);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col gap-0.5 max-w-[200px] cursor-default">
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                <XCircle className="h-3 w-3 shrink-0" />
                Rejected
              </span>
              <span className="text-xs text-red-600/80 truncate pl-[16px]">
                {video.reject_reason || 'No reason given'}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs space-y-1.5">
            {tagLabels.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tagLabels.map(label => (
                  <span
                    key={label}
                    className="rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700"
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
            <p>{video.reject_reason || 'No reason given'}</p>
            {video.reject_reason_other && (
              <p><span className="font-medium text-foreground">Other: </span>{video.reject_reason_other}</p>
            )}
            {video.reviewed_by && (
              <p className="text-muted-foreground">
                Rejected by {video.review_source === 'brand' ? 'the brand' : 'BMC'} · {video.reviewed_by}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Once the hire's money is closed — the creator was paid, OR the brand was
  // refunded in full short of delivery — its videos are locked. A rejection there
  // would try to claw back money that is already gone; that needs a manual
  // decision, not a one-click action. See payoutClosed() for why this is not
  // settled_at.
  if (hireLocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs text-muted-foreground italic cursor-default">Locked</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            The money on this hire is closed — the creator was paid, or the brand was refunded in
            full. Approving or rejecting a video can&apos;t change that, so review is off.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        {error && !dialogOpen && <p className="text-[11px] text-red-500">{error}</p>}
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            disabled={loading}
            onClick={() => { setError(null); setDialogMode('approved'); setDialogOpen(true); }}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs text-red-700 border-red-200 hover:bg-red-50"
            disabled={loading}
            onClick={() => { setError(null); setDialogMode('rejected'); setDialogOpen(true); }}
          >
            Reject
          </Button>
        </div>
      </div>

      <ReviewVideoDialog
        open={dialogOpen}
        onOpenChange={o => { setDialogOpen(o); if (!o) setError(null); }}
        mode={dialogMode}
        videoUrl={video.tiktok_video_url}
        loading={loading}
        error={error}
        flagReason={activeFlagReason}
        isViralRemake={campaign?.is_viral_remake}
        referenceVideos={campaign?.brief_video_urls}
        onConfirm={(reason, tags, otherReason) => applyReview(dialogMode, reason, tags, otherReason)}
      />
    </>
  );
}

/* ============================================================================
 * Bulk review — one decision applied to several videos at once
 * ============================================================================
 * Exactly the write VideoReviewCell makes for a single video (review_status +
 * reject_reason, and answering the brand's flag when the video carries one),
 * applied to every checked row. ONE typed reason covers the whole batch — on a
 * rejection it is the copy the creator reads on each video, on a flagged
 * approval it is what the brand reads — so a batch has to be videos that share
 * the same verdict for the same reason, not a way to clear the table.
 *
 * Approve is the heavier of the two: it completes the hire and unlocks the
 * payout (notify_video_submission_approved →
 * 20260821000000_cr_hires_complete_on_approval.sql, and payoutState). So it
 * keeps every guard the single-video path has — most of all the written reason
 * a FLAGGED video demands, since approving one overrules the brand and they
 * read that text back. Nothing is approved here that couldn't be approved one
 * click at a time; it's the same decision, typed once.
 *
 * Selection never leaves the current page: the rows are the ones the table query
 * loaded, and answering their flags needs each row's brand_flag_* fields. Any
 * change to page, size, search or sort drops it (see HireDetail).
 * ========================================================================== */

/** The rows checkboxes are offered for — undecided, still live on TikTok, on a
 *  hire whose money is still open. Mirrors exactly when VideoReviewCell renders
 *  its Approve/Reject buttons, so nothing is selectable that isn't reviewable. */
function isBulkSelectable(video: VideoSubmission, hireLocked: boolean) {
  return !hireLocked && !video.deleted && video.review_status == null;
}

type BulkReviewOutcome = {
  reviewed: number;
  /** Rows already decided by the time the update ran — another admin got there
   *  first. Their call stands; nothing here overwrote it. */
  skipped: number;
  /** Reviewed videos whose brand flag could not be closed out. The decision
   *  stands; the flag is still answerable from the flag card above. */
  flagsLeftOpen: number;
};

async function bulkReviewVideos(
  videos: VideoSubmission[],
  hireId: string,
  status: ReviewMode,
  reason: string,
  tags: RejectReasonTag[] = [],
  otherReason?: string,
): Promise<BulkReviewOutcome> {
  const trimmed = reason.trim();
  const ids = videos.map(v => v.id);
  const { data: { session } } = await supabase.auth.getSession();
  const adminEmail = session?.user?.email ?? 'admin';
  const payload = {
    review_status: status,
    reviewed_at: new Date().toISOString(),
    reviewed_by: adminEmail,
    review_source: 'admin' as const,
    // Same as the single path: an approval note has nowhere to live on the
    // review columns — when the video is flagged it goes onto the flag's
    // resolution instead (below), which is the copy the brand reads.
    reject_reason: status === 'rejected' ? (trimmed || null) : null,
    // Same shape as the single-video path (ReviewVideoDialog) — every video
    // in a bulk batch shares one hire, so one campaign, so one remake status
    // and one valid tag set applies to all of them.
    reject_reason_tags: status === 'rejected' && tags.length ? tags : null,
    reject_reason_other: status === 'rejected' && tags.includes('other') ? (otherReason?.trim() || null) : null,
  };

  // `.is('review_status', null)` is the concurrency guard — the same shape
  // resolveVideoFlag uses. A row someone else decided since this page loaded
  // simply doesn't match, so their decision survives and the row comes back as
  // skipped instead of being silently overwritten. hire_id and deleted repeat
  // the client-side eligibility test server-side.
  const { data, error } = await supabase
    .from('cr_video_submissions')
    .update(payload)
    .in('id', ids)
    .eq('hire_id', hireId)
    .eq('deleted', false)
    .is('review_status', null)
    .select('id');

  if (error) {
    // One entry for the batch rather than one per video: nothing was written,
    // and the attempted ids are the useful record. record_id stays null instead
    // of naming an arbitrary row.
    await logAdminAction({
      action: 'UPDATE',
      tableName: 'cr_video_submissions',
      newValues: payload,
      metadata: {
        status: 'failed',
        error: error.message,
        description: `Failed to bulk ${status === 'approved' ? 'approve' : 'reject'} ${ids.length} video submission${ids.length !== 1 ? 's' : ''}${trimmed ? ` — reason: ${trimmed}` : ''}`,
        hire_id: hireId,
        video_ids: ids,
        bulk: true,
      },
    });
    throw error;
  }

  const updatedIds = new Set((data ?? []).map((r: { id: string }) => r.id));
  const reviewed = videos.filter(v => updatedIds.has(v.id));

  // Per-video audit entries — the trail is searched by record, so a bulk
  // decision has to leave the same row-level history a single one does. That
  // it was a batch lives in the metadata.
  await Promise.all(reviewed.map(v => logUpdate('cr_video_submissions', v.id, {
    review_status: v.review_status,
    reject_reason: v.reject_reason,
  }, payload, {
    status: 'success',
    description: `Admin ${status} a video submission (bulk of ${reviewed.length})${trimmed ? ` — reason: ${trimmed}` : ''}`,
    hire_id: hireId,
    tiktok_video_url: v.tiktok_video_url,
    bulk: true,
  })));

  // Flag feature retired 2026-09-28 — the brand can no longer raise a flag,
  // so there is nothing left here to resolve. Commented out, not deleted, in
  // case it comes back.
  // // Answer each brand flag with the same decision the single path gives it:
  // // approve → dismissed, carrying the reason the dialog required; reject →
  // // upheld, reusing the reject reason so the brand and the creator are told the
  // // same thing. Non-fatal for the same reason it is there: the review is the
  // // decision that moves the hire, and a flag left open is recoverable from the
  // // flag card.
  // const flagged = reviewed.filter(isFlagActive);
  // const flagResults = await Promise.all(flagged.map(v => resolveVideoFlag(v, {
  //   resolution: status === 'approved' ? 'dismissed' : 'upheld',
  //   notes: trimmed,
  //   hireId,
  // })));

  return {
    reviewed: reviewed.length,
    skipped: ids.length - reviewed.length,
    // Always 0 now — see the comment above; nothing here answers a flag any more.
    flagsLeftOpen: 0,
  };
}

/** Whether a batch can be confirmed as typed, and why not when it can't. Same
 *  rules as the single-video dialog, counted over the selection: a rejection
 *  needs at least one reason tag plus a note past MIN_REJECT_NOTE (and, if
 *  'other' is picked, its own box past MIN_REJECT_OTHER_NOTE) — identical
 *  floor to ReviewVideoDialog's rejectBlocked, just applied to the whole
 *  batch at once. An approval needs a reason once ANY video in the batch is
 *  flagged — that text is the brand's answer, and resolveVideoFlag rejects
 *  anything shorter than MIN_FLAG_RESOLUTION_NOTES, which would otherwise leave
 *  the flags open after the approval had already landed. */
function bulkReviewBlocker(
  mode: ReviewMode,
  flaggedCount: number,
  reason: string,
  selectedTags: RejectReasonTag[],
  otherReason: string,
): string | null {
  const trimmed = reason.trim();
  if (mode === 'rejected') {
    if (selectedTags.length === 0) return 'Select at least one reason for the rejection.';
    if (trimmed.length < MIN_REJECT_NOTE) {
      return `Write why these videos are being rejected — at least ${MIN_REJECT_NOTE} characters. The creator sees it.`;
    }
    if (selectedTags.includes('other') && otherReason.trim().length < MIN_REJECT_OTHER_NOTE) {
      return `Explain what "other" means — at least ${MIN_REJECT_OTHER_NOTE} characters. Not shown to the creator.`;
    }
    return null;
  }
  if (flaggedCount === 0) return null;
  return trimmed.length >= MIN_FLAG_RESOLUTION_NOTES
    ? null
    : `Write why you're approving ${flaggedCount !== 1 ? 'videos the brand flagged' : 'a video the brand flagged'} — at least ${MIN_FLAG_RESOLUTION_NOTES} characters. The brand sees it.`;
}

// The confirm step for a bulk decision. Same hero, same reason rules and same
// copy as ReviewVideoDialog — what it adds is the list of what's in the batch,
// because the whole risk of a batch action is not knowing what's in it. A
// reject also gets the same reason-tag picker as the single-video dialog:
// every video here shares one hire, so one campaign and one remake status,
// so the same tag set is valid for the whole batch.
function BulkReviewDialog({ open, onOpenChange, mode, videos, loading, error, onConfirm, isViralRemake, referenceVideos }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ReviewMode;
  videos: VideoSubmission[];
  loading: boolean;
  error: string | null;
  onConfirm: (reason: string, tags: RejectReasonTag[], otherReason?: string) => void;
  isViralRemake?: boolean | null;
  referenceVideos?: { url: string; name?: string | null; description?: string | null }[] | null;
}) {
  const [reason, setReason] = useState('');
  const [selectedTags, setSelectedTags] = useState<RejectReasonTag[]>([]);
  const [otherReason, setOtherReason] = useState('');
  const [showInvalidReasons, setShowInvalidReasons] = useState(false);

  useEffect(() => {
    if (!open) { setReason(''); setSelectedTags([]); setOtherReason(''); setShowInvalidReasons(false); }
  }, [open, mode]);

  const tone = REVIEW_DIALOG_TONES[mode];
  const Icon = tone.icon;
  const count = videos.length;
  // Flag feature retired 2026-09-28 — always 0 now, so the flag-aware copy
  // below never shows. Commented out, not deleted, in case it comes back.
  // const flaggedCount = videos.filter(isFlagActive).length;
  const flaggedCount: number = 0;
  const approving = mode === 'approved';
  const remake = isViralRemake === true;
  const otherSelected = selectedTags.includes('other');
  const needsReason = !approving || flaggedCount > 0;
  const blocker = bulkReviewBlocker(mode, flaggedCount, reason, selectedTags, otherReason);

  function toggleTag(tag: RejectReasonTag) {
    setSelectedTags(prev => {
      const next = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      if (tag === 'other' && !next.includes('other')) setOtherReason('');
      return next;
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={o => { if (!loading) onOpenChange(o); }}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
        {/* min-w-0 throughout — AlertDialogContent is a grid, and the video URLs
            are long unbreakable tokens. See ReviewVideoDialog. */}
        <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${tone.banner} px-5 py-4 text-white`}>
          <div className="pointer-events-none absolute -left-10 -top-12 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="relative flex items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-4 ${tone.iconClass}`}>
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 space-y-0.5">
              <AlertDialogTitle className="text-lg font-semibold leading-tight tracking-tight text-white">
                {approving ? 'Approve' : 'Reject'} {count} video{count !== 1 ? 's' : ''}?
              </AlertDialogTitle>
              <p className="text-xs text-white/80">
                {approving
                  ? 'Confirms every selected submission as delivered for this hire'
                  : 'One reason, applied to every selected submission'}
              </p>
            </div>
          </div>
        </div>

        <AlertDialogDescription asChild>
          <div className="min-w-0 space-y-3 text-sm text-muted-foreground">
            <p>
              {approving ? (
                <>
                  Approving locks {count !== 1 ? 'these videos' : 'this video'} in as delivered.
                  {count !== 1 ? ' They stay' : ' It stays'} on the campaign and count
                  {count !== 1 ? '' : 's'} toward the creator&apos;s payout for this hire.
                  This action can&apos;t be undone.
                </>
              ) : (
                <>
                  The creator will be notified right away and asked to submit
                  {count !== 1 ? ` ${count} replacement videos` : ' a replacement video'} for this campaign.
                  This action can&apos;t be undone.
                </>
              )}
            </p>
            <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-lg border bg-muted/40 p-2">
              {videos.map(v => (
                <a
                  key={v.id}
                  href={v.tiktok_video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-muted"
                >
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-colors group-hover:text-primary" />
                  <span className="truncate text-xs text-muted-foreground group-hover:text-foreground" title={v.tiktok_video_url}>
                    {v.tiktok_video_url}
                  </span>
                  {/* Flag feature retired 2026-09-28 — commented out, not deleted. */}
                  {/* {isFlagActive(v) && <Flag className="h-3 w-3 shrink-0 text-red-600" fill="currentColor" />} */}
                </a>
              ))}
            </div>
          </div>
        </AlertDialogDescription>

        {/* The brand objected to some of these. Approving overrules them and
            clears the flag; rejecting upholds it. Either way the reason below
            is copied into the flag, so the brand reads the same words. */}
        {flaggedCount > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" fill="currentColor" />
            <span>
              {flaggedCount} of these {flaggedCount !== 1 ? 'are' : 'is'} flagged by the brand.{' '}
              {approving
                ? `Approving overrules them and clears ${flaggedCount !== 1 ? 'those flags' : 'that flag'} — they read your reason on the video.`
                : `Rejecting upholds ${flaggedCount !== 1 ? 'those flags' : 'that flag'} and shows them this reason.`}
            </span>
          </p>
        )}

        {error && <p className="text-xs text-red-500">{error}</p>}

        {/* Reject-only additions — same shape as ReviewVideoDialog: job type,
            the campaign's reference video(s) on a remake, the brief reminder,
            the reason chips, and the collapsible "not valid" list, all ahead
            of the text box. One set applies to the whole batch since every
            video here is on the same hire/campaign. */}
        {mode === 'rejected' && (
          <div className="space-y-3">
            <span className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${
              remake ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-muted-foreground/20 bg-muted text-muted-foreground'
            }`}>
              {remake ? 'Viral video remake' : 'Regular video'}
            </span>

            {remake && referenceVideos && referenceVideos.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-2.5 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {referenceVideos.length === 1 ? 'Original video' : 'Original videos'}
                </p>
                {referenceVideos.map((rv, i) => (
                  <a
                    key={rv.url || i}
                    href={rv.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex min-w-0 items-center gap-1.5 text-xs text-foreground hover:text-primary"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/70 group-hover:text-primary" />
                    <span className="truncate">{rv.name?.trim() || 'View original'}</span>
                  </a>
                ))}
              </div>
            )}

            <p className="text-xs leading-relaxed text-muted-foreground">{rejectBriefReminder(remake)}</p>

            <div className="space-y-2">
              <p className="text-sm font-medium">
                Select a reason <span className="font-normal text-muted-foreground">(required — pick at least one)</span>
              </p>
              <div className="space-y-1.5">
                {rejectReasonTagsFor(remake).map(opt => {
                  const checked = selectedTags.includes(opt.key);
                  return (
                    <Fragment key={opt.key}>
                    <label
                      className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs leading-relaxed cursor-pointer ${
                        checked ? 'border-primary bg-primary/5' : 'border-input'
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleTag(opt.key)}
                        className="mt-0.5"
                      />
                      <span>{opt.label}</span>
                    </label>
                    {opt.key === 'other' && checked && (
                      <div className="ml-2 border-l-2 border-primary/20 pl-3 space-y-1">
                        <textarea
                          value={otherReason}
                          onChange={e => setOtherReason(e.target.value)}
                          rows={2}
                          maxLength={MAX_REJECT_OTHER_NOTE}
                          placeholder={otherReasonPlaceholder(remake)}
                          className="w-full rounded-md border text-xs px-2.5 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-200"
                        />
                        <p className={`text-[11px] ${
                          otherReason.trim().length > 0 && otherReason.trim().length < MIN_REJECT_OTHER_NOTE
                            ? 'text-red-500'
                            : 'text-muted-foreground'
                        }`}>
                          {otherReason.trim().length > 0 && otherReason.trim().length < MIN_REJECT_OTHER_NOTE
                            ? `A few more words — at least ${MIN_REJECT_OTHER_NOTE} characters.`
                            : `${otherReason.length}/${MAX_REJECT_OTHER_NOTE} — not shown to the creator`}
                        </p>
                      </div>
                    )}
                    </Fragment>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowInvalidReasons(v => !v)}
              className="text-xs font-medium text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
            >
              What counts as a valid rejection?
            </button>
            {showInvalidReasons && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground">Not valid reasons: </strong>
                {rejectNotValidReasons(remake)}
              </p>
            )}
          </div>
        )}

        {needsReason && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              {mode === 'rejected' ? 'What needs to be fixed?' : 'Reason'}{' '}
              <span className="font-normal text-muted-foreground">
                {approving
                  ? '(shown to the brand)'
                  : flaggedCount > 0 ? '(shown to the creator and the brand)' : '(shown to the creator)'}
              </span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={approving
                ? "e.g. Checked the brief — these videos meet what this creator was briefed on, so we're paying them out."
                : rejectNotePlaceholder(remake)}
              rows={3}
              maxLength={approving ? MAX_FLAG_RESOLUTION_NOTES : 2000}
              autoFocus
              className={`w-full resize-none rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                approving ? 'focus:ring-emerald-200' : 'focus:ring-red-200'
              }`}
            />
            <p className="text-xs text-muted-foreground">
              {mode === 'rejected' && reason.trim().length > 0 && reason.trim().length < MIN_REJECT_NOTE && (
                <span className="text-red-500">A few more words — at least {MIN_REJECT_NOTE} characters. </span>
              )}
              The same text goes on all {count} video{count !== 1 ? 's' : ''} — review them separately if
              they need different answers.
            </p>
          </div>
        )}

        {mode === 'rejected' && (
          <p className="rounded-md border bg-muted/40 px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
            <strong className="text-foreground">What happens next: </strong>
            These videos stop counting toward the job while under review. The creator can
            remake them, dispute the rejection, or drop them from the package for a reduced payout.
          </p>
        )}

        <AlertDialogFooter>
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {approving ? (
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={loading || !!blocker}
              title={blocker ?? undefined}
              onClick={() => onConfirm(reason, [])}
            >
              {loading ? 'Approving…' : `Approve ${count} video${count !== 1 ? 's' : ''}`}
            </Button>
          ) : (
            <Button
              variant="destructive"
              disabled={loading || !!blocker}
              title={blocker ?? undefined}
              onClick={() => onConfirm(reason, selectedTags, otherSelected ? otherReason : undefined)}
            >
              {loading ? 'Rejecting…' : `Reject ${count} video${count !== 1 ? 's' : ''}`}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// The strip above the table, shown only while something is checked. It owns the
// write so the table itself stays a table; the outcome goes to a toast because
// clearing the selection unmounts this bar with it.
function BulkReviewBar({ videos, hireId, onClear, isViralRemake, referenceVideos }: {
  videos: VideoSubmission[];
  hireId: string;
  onClear: () => void;
  isViralRemake?: boolean | null;
  referenceVideos?: { url: string; name?: string | null; description?: string | null }[] | null;
}) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState<ReviewMode>('approved');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = videos.length;
  // Flag feature retired 2026-09-28 — always 0 now, so the "N flagged" pill
  // below never shows. Commented out, not deleted, in case it comes back.
  // const flaggedCount = videos.filter(isFlagActive).length;
  const flaggedCount: number = 0;

  const open = (next: ReviewMode) => { setError(null); setMode(next); setDialogOpen(true); };

  const confirm = async (reason: string, tags: RejectReasonTag[], otherReason?: string) => {
    setLoading(true);
    setError(null);
    try {
      const outcome = await bulkReviewVideos(videos, hireId, mode, reason, tags, otherReason);
      const verb = mode === 'approved' ? 'approved' : 'rejected';

      if (outcome.reviewed > 0) {
        toast.success(`${outcome.reviewed} video${outcome.reviewed !== 1 ? 's' : ''} ${verb}`, {
          description: outcome.flagsLeftOpen > 0
            ? `${outcome.flagsLeftOpen} brand flag${outcome.flagsLeftOpen !== 1 ? 's' : ''} stayed open — answer them from the flag panel above.`
            : undefined,
        });
      }
      // Rows another admin decided first. Worth saying out loud: the admin asked
      // for N decisions and got fewer, and the difference is someone else's call
      // that this deliberately did not overwrite.
      if (outcome.skipped > 0) {
        toast.warning(`${outcome.skipped} video${outcome.skipped !== 1 ? 's were' : ' was'} already reviewed`, {
          description: 'Another admin decided them first, so they were left as they are.',
        });
      }

      setDialogOpen(false);
      onClear();
      queryClient.invalidateQueries({ queryKey: ['hire-videos', hireId] });
      queryClient.invalidateQueries({ queryKey: ['hire-stats', hireId] });
      queryClient.invalidateQueries({ queryKey: ['hire-flags', hireId] });
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${mode === 'approved' ? 'approve' : 'reject'} the selected videos`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/50 px-5 py-2.5">
        <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {count} video{count !== 1 ? 's' : ''} selected
        </span>
        {/* Says up front that the batch needs an answer to the brand, before the
            admin picks a verdict and finds the confirm button disabled. */}
        {flaggedCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-red-700">
            <Flag className="h-3 w-3" fill="currentColor" />
            {flaggedCount} flagged
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            className="h-7 bg-emerald-600 px-3 text-xs text-white hover:bg-emerald-700"
            onClick={() => open('approved')}
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Approve selected
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-7 px-3 text-xs"
            onClick={() => open('rejected')}
          >
            <XCircle className="mr-1 h-3.5 w-3.5" />
            Reject selected
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onClear}>
            Clear
          </Button>
        </div>
      </div>

      <BulkReviewDialog
        open={dialogOpen}
        onOpenChange={o => { setDialogOpen(o); if (!o) setError(null); }}
        mode={mode}
        videos={videos}
        loading={loading}
        error={error}
        onConfirm={confirm}
        isViralRemake={isViralRemake}
        referenceVideos={referenceVideos}
      />
    </>
  );
}


export default function HireDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Back has to return ops to the list AS THEY LEFT IT — tab, money lens, sort,
  // page, search all live in the query string now (see CreatorPayments). A plain
  // navigate('/creator-payments') drops every one of them, which is exactly the
  // annoyance the query string was added to fix: set up a view, open a hire, come
  // back to an unfiltered table.
  //
  // History already holds the precise URL, so step back through it. The fallback
  // is only for when there is no history to step into — a pasted link, a refresh,
  // or a new tab — which is what location.key === 'default' means.
  //
  // RosterCreatorDetail links into this page too, so stepping back can land
  // somewhere other than Creator Payments — hence the label follows the
  // destination instead of naming one it might not go to.
  const cameFromApp = location.key !== 'default';
  const backToPayments = () => cameFromApp ? navigate(-1) : navigate('/creator-payments');
  const backLabel = cameFromApp ? 'Back' : 'Creator Payments';
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [sortField, setSortField]     = useState<SortField>('submitted_at');
  const [sortDir, setSortDir]         = useState<SortDir>('asc');
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(10);
  // Checked rows for a bulk approve/reject — ids only; the rows themselves come
  // from the table query below. See the bulk-review block above for why a
  // selection can't outlive the page it was made on.
  const [selectedIds, setSelectedIds]  = useState<string[]>([]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Anything that changes WHICH rows are on screen drops the selection: a
  // checkbox the admin can no longer see is not a decision they can still vouch
  // for, and the reject needs the loaded row to answer its brand flag.
  useEffect(() => { setSelectedIds([]); }, [id, page, pageSize, search, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  }

  // Hire header query — single fetch, no pagination needed
  const { data: hire, isLoading: hireLoading } = useQuery<Hire>({
    queryKey: ['hire-detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_hires')
        .select(`*, roster_creators(name, tiktok_username, email), cr_campaigns(id, name, product_name, product_id, is_multi_product, bundle_product_id, bundle_product_name, is_viral_remake, brief_video_urls), cr_hire_payouts(${TROLLEY_PAYOUT_COLUMNS})`)
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as Hire;
    },
    enabled: !!id,
  });

  // Stats query — all submissions for this hire (lightweight, not paginated)
  const { data: allStats = [] } = useQuery<VideoStat[]>({
    queryKey: ['hire-stats', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_video_submissions')
        .select('product_match, review_status, review_source, brand_reaction, reviewed_at, views, gmv_cents, orders, submitted_product_id, submitted_product_name, matched_product_id, matched_product_name')
        // Removed-from-TikTok videos can't earn payout and don't count toward
        // videos_required — excluded here so every stat below (Submitted,
        // Matched, Total Views/GMV) reflects only still-eligible videos.
        .eq('deleted', false)
        .eq('hire_id', id);
      if (error) throw error;
      return (data ?? []) as VideoStat[];
    },
    enabled: !!id,
  });

  // Paginated table query — server-side search + sort + pagination
  const { data: videosResult, isLoading: videosLoading } = useQuery<{ rows: VideoSubmission[]; total: number }>({
    queryKey: ['hire-videos', id, search, sortField, sortDir, page, pageSize],
    queryFn: async () => {
      let query = supabase
        .from('cr_video_submissions')
        .select(
          `id, hire_id, tiktok_video_url, tiktok_video_id, product_match, submitted_product_id, submitted_product_name, matched_product_id, matched_product_name, review_status, review_source, brand_reaction, brand_reaction_note, reject_reason, reject_reason_tags, reject_reason_other, reviewed_at, reviewed_by, ownership_verified, ownership_verified_at, views, gmv_cents, orders, units_sold, click_through_rate, last_synced_at, last_sync_error, submitted_at, deleted, ${VIDEO_FLAG_COLUMNS}, ${ADMIN_FLAG_COLUMNS}`,
          { count: 'exact' }
        )
        // Match the stats query above — hide removed-from-TikTok videos so
        // the admin's video table agrees with the Submitted/Matched counts.
        .eq('deleted', false)
        .eq('hire_id', id)
        .order(sortField, { ascending: sortDir === 'asc' });

      if (search.trim()) {
        query = query.ilike('tiktok_video_url', `%${search.trim()}%`);
      }

      const { data, count, error } = await query.range((page - 1) * pageSize, page * pageSize - 1);
      if (error) throw error;
      return { rows: (data ?? []) as VideoSubmission[], total: count ?? 0 };
    },
    enabled: !!id,
  });

  // Videos removed from TikTok. Every other query on this page filters these
  // out on purpose — they earn no payout and free their delivery slot, so
  // letting them touch Submitted/Matched/Delivered would misstate the hire.
  //
  // They are still the most important rows on a reopened hire: a deletion is
  // what silently sends a completed hire back to 'confirmed', and until this
  // section existed ops had no way to see that had happened. Read-only, counted
  // nowhere, listed separately below the table.
  const { data: deletedVideos = [] } = useQuery<DeletedVideo[]>({
    queryKey: ['hire-deleted-videos', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_video_submissions')
        .select('id, tiktok_video_url, tiktok_video_id, review_status, review_source, reviewed_at, submitted_at, deleted_at, last_synced_at, views, gmv_cents')
        .eq('hire_id', id)
        .eq('deleted', true)
        // Most recently removed first — the one that just reopened the hire.
        .order('deleted_at', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as DeletedVideo[];
    },
    enabled: !!id,
  });
  // The campaign's own product list, for the "Videos by product" breakdown.
  // Named from cr_campaign_products rather than from whatever TikTok echoed
  // back on the submission, so the breakdown uses the brand's own product names
  // — and so a product with ZERO videos still appears, which is the half of the
  // picture the submissions alone can never show ("which product has the
  // creator not covered yet?"). Only fetched for a multi-product campaign;
  // there is nothing to break down on a single-product hire.
  const campaignId = hire?.cr_campaigns?.id ?? null;
  const isMultiProductCampaign = hire?.cr_campaigns?.is_multi_product === true;
  const { data: campaignProducts = [] } = useQuery<CampaignProductRow[]>({
    queryKey: ['hire-campaign-products', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_campaign_products')
        .select('id, product_id, product_name, product_image_urls, sort_order')
        .eq('campaign_id', campaignId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CampaignProductRow[];
    },
    enabled: !!campaignId && isMultiProductCampaign,
  });

  // Every flagged video on this hire, regardless of which page of the table
  // it's on — the flag panel is what ops has to answer, so it can't be hostage
  // to pagination. Filtered server-side to flagged rows only, which on most
  // hires means zero rows come back.
  type FlaggedVideoRow = VideoFlagFields & {
    id: string; tiktok_video_url: string; review_status: 'approved' | 'rejected' | null;
  };
  const { data: flaggedVideos = [] } = useQuery<FlaggedVideoRow[]>({
    queryKey: ['hire-flags', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_video_submissions')
        .select(`id, tiktok_video_url, review_status, ${VIDEO_FLAG_COLUMNS}`)
        .eq('hire_id', id)
        .eq('deleted', false)
        .not('brand_flag_status', 'is', null)
        .order('brand_flagged_at', { ascending: false });
      if (error) {
        // Columns absent (migration not applied in this environment yet) — the
        // rest of the hire page must still render.
        if (error.code === '42703' || error.code === '42P01') return [];
        throw error;
      }
      return (data ?? []) as unknown as FlaggedVideoRow[];
    },
    enabled: !!id,
  });

  // Invite/lifecycle event timeline for this hire — read-only history from
  // cr_creator_events, populated automatically by DB triggers (see migration
  // 20260713000004_cr_creator_activity_events.sql). Never written to from here.
  const { data: events = [], isLoading: eventsLoading } = useQuery<HireEvent[]>({
    queryKey: ['hire-events', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_creator_events')
        .select('id, event_type, occurred_at, metadata')
        .eq('hire_id', id)
        .order('occurred_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as HireEvent[];
    },
    enabled: !!id,
  });

  // Every counter-offer round on this hire, oldest first — read-only, worker-
  // written (see cr_counter_offers' RLS: SELECT only, no admin write path).
  // Only ever non-empty for an invite-lane negotiation; an application-lane
  // one hangs off the application instead and this hire may not even exist
  // yet while that's still live (see RosterCampaignDetail's Applications tab
  // for that side).
  const { data: counterOffers = [], isLoading: counterOffersLoading } = useQuery<CounterOfferAdminRow[]>({
    queryKey: ['hire-counter-offers', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_counter_offers')
        .select('id, sender, round, package_size, creator_rate_cents, brand_rate_cents, with_rights, note, status, decline_reason, created_at')
        .eq('hire_id', id)
        .order('created_at', { ascending: true });
      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }
      return (data ?? []) as CounterOfferAdminRow[];
    },
    enabled: !!id,
  });

  const videos    = videosResult?.rows ?? [];
  const total     = videosResult?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Stats always from full unfiltered set
  const videosRequired = hire?.videos_required ?? hire?.package_size ?? 0;
  const matched      = allStats.filter(v => v.product_match === true).length;
  const unmatched    = allStats.filter(v => v.product_match !== true).length;
  // Videos that count toward the requirement — allStats already excludes deleted
  // ones, so only admin-rejected submissions need dropping. Same definition as
  // hire_delivered_video_count() in the DB; the refund math depends on it.
  const delivered    = allStats.filter(v => v.review_status !== 'rejected').length;
  // The slice of those counting videos still awaiting approval. Every video must
  // be approved before a payout — the Pay button stays off until this is 0.
  const pendingReview = payoutSubmissionCounts(allStats).pending;
  const needsReview  = allStats.filter(v => v.product_match === false && v.review_status == null).length;
  // Most recent rejection on record, if any — RejectedVideoDeadlineBanner
  // needs the timing of the rejection itself, not just that one exists.
  const lastRejectedAt = allStats
    .filter(v => v.review_status === 'rejected' && v.reviewed_at)
    .reduce<string | null>((latest, v) => (!latest || v.reviewed_at! > latest ? v.reviewed_at! : latest), null);
  // The CREATOR's submission window — closed once the creator was paid out or
  // the hire was settled, a partial refund included. Deliberately NOT the same
  // test as hireLocked below: settled_at means the creator can no longer submit
  // anything, so offering them more time is meaningless, while ops may still
  // have videos to review and a payout to release on that same hire. The two
  // predicates shared a name (`hireLocked`) across two branches and collided in
  // the merge; they were never the same question. See VideoReviewCell.
  const creatorWindowClosed = (hire?.payment_status ?? 'unpaid') === 'paid' || !!hire?.settled_at;
  const totalViews  = allStats.reduce((s, v) => s + (v.views ?? 0), 0);
  const totalGmv    = allStats.reduce((s, v) => s + (v.gmv_cents ?? 0), 0);
  const totalOrders = allStats.reduce((s, v) => s + (v.orders ?? 0), 0);

  // payoutClosed, NOT settled_at — a partial refund stamps settled_at and still
  // owes the creator a payout that can't be recorded until these videos are
  // reviewed. See the comment on VideoReviewCell.
  const hireLocked  = !!hire && payoutClosed(hire, delivered);
  // The rows a bulk decision can act on. Empty means every video on this page
  // is already settled (reviewed, or gone from TikTok) — the checkbox column
  // hides entirely rather than standing there with nothing to check.
  const selectable  = videos.filter(v => isBulkSelectable(v, hireLocked));
  const showSelect  = selectable.length > 0;

  // Drop checked rows that stopped being reviewable — another admin decided
  // them, or a sync marked them deleted — so a stale tick can't sit in a
  // selection the admin then acts on.
  useEffect(() => {
    setSelectedIds(ids => {
      const live = new Set(selectable.map(v => v.id));
      const next = ids.filter(x => live.has(x));
      return next.length === ids.length ? ids : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videosResult, hireLocked]);

  if (hireLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!hire) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
        <p className="text-lg font-medium">Hire not found</p>
        <Button variant="outline" onClick={backToPayments}>
          <ChevronLeft className="h-4 w-4 mr-1" /> {cameFromApp ? 'Back' : 'Back to Payments'}
        </Button>
      </div>
    );
  }

  const selected     = videos.filter(v => selectedIds.includes(v.id));
  const allSelected  = selectable.length > 0 && selectable.every(v => selectedIds.includes(v.id));
  const toggleAll    = () => setSelectedIds(allSelected ? [] : selectable.map(v => v.id));
  const toggleOne    = (videoId: string) => setSelectedIds(
    ids => ids.includes(videoId) ? ids.filter(x => x !== videoId) : [...ids, videoId]
  );

  const creatorName  = hire.roster_creators?.name ?? '—';
  const tiktokHandle = hire.roster_creators?.tiktok_username;
  const campaignName = hire.cr_campaigns?.name ?? hire.campaign_id;
  const productName  = hire.cr_campaigns?.product_name;

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <div>
        <Button variant="ghost" size="sm" className="gap-1 -ml-2 text-muted-foreground" onClick={backToPayments}>
          <ChevronLeft className="h-4 w-4" /> {backLabel}
        </Button>
      </div>

      {/* Hire header */}
      <div className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-start gap-3 justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
              <button
                className="hover:underline text-left"
                onClick={() => navigate(`/roster-creators/${hire.creator_id}`)}
                title="View creator profile"
              >
                {creatorName}
              </button>
            </h1>
            {tiktokHandle && <p className="text-sm text-muted-foreground">@{tiktokHandle}</p>}
            {hire.roster_creators?.email && <p className="text-xs text-muted-foreground">{hire.roster_creators.email}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {isViralRemake(hire.cr_campaigns) && <ViralRemakeBadge />}
            <HireStatusBadge status={hire.status} />
            {(hire.payment_status ?? 'unpaid') === 'paid'
              ? <Badge className="bg-green-100 text-green-800 border border-green-200">Paid</Badge>
              : <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Unpaid</Badge>
            }
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Campaign</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="font-medium truncate">{campaignName}</p>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs break-words text-xs">{campaignName}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {productName && (
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Product</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="font-medium truncate">{productName}</p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs break-words text-xs">{productName}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isViralRemake(hire.cr_campaigns) && <ViralRemakeBadge className="mt-1.5 whitespace-nowrap" />}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Creator Rate</p>
            <p className="font-medium truncate">{fmtCents(hire.creator_rate_cents)}</p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">Videos Required</p>
            <p className="font-medium">{videosRequired}</p>
          </div>
          {hire.confirmed_at && (
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Confirmed</p>
              <p className="font-medium">{format(new Date(hire.confirmed_at), 'MMM d, yyyy')}</p>
            </div>
          )}
          {hire.completed_at && (
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-0.5">Completed</p>
              <p className="font-medium">{format(new Date(hire.completed_at), 'MMM d, yyyy')}</p>
            </div>
          )}
          {/* Paid At and Transaction ID used to sit here. They live in the Creator
              Payout panel below now, alongside the amount, who paid it and whether
              it was partial — the same split RefundPanel already owns for the brand
              side, so neither half of a hire's money is told in two places. */}
        </div>

        {/* Deadline section — only for active/failed confirmed hires */}
        {(hire.status === 'confirmed' || hire.status === 'failed') && (
          <>
            <Separator />
            <DeadlinePanel hire={hire} onGranted={() => queryClient.invalidateQueries({ queryKey: ['hire-detail', id] })} />
          </>
        )}

        {/* Brand money — always shown once anything was collected, so ops can
            see the funding split even on hires that will never be refunded. */}
        <Separator />
        <RefundPanel
          hire={hire}
          deliveredVideos={delivered}
          onRefunded={() => {
            queryClient.invalidateQueries({ queryKey: ['hire-detail', id] });
            queryClient.invalidateQueries({ queryKey: ['hire-stats', id] });
          }}
        />

        {/* Creator money, second — the two panels are in workflow order. A partial
            payout is blocked until the brand has been refunded, so when it says
            "Refund brand first" the button that does it is immediately above.
            (Its separator is inside the component, which can render nothing.) */}
        <PayoutPanel
          hire={hire}
          deliveredVideos={delivered}
          pendingVideos={pendingReview}
          onPaid={() => {
            queryClient.invalidateQueries({ queryKey: ['hire-detail', id] });
            queryClient.invalidateQueries({ queryKey: ['hire-stats', id] });
          }}
        />
      </div>

      {/* Stats — always from full unfiltered set */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={Video}        label="Required"    value={videosRequired} />
        <StatCard icon={Clock}        label="Submitted"   value={allStats.length} sub={videosRequired - allStats.length > 0 ? `${videosRequired - allStats.length} remaining` : 'all submitted'} />
        <StatCard icon={CheckCircle2} label="Matched"     value={matched}     color="text-green-600" sub="product matched" />
        <StatCard icon={XCircle}      label="Not Matched" value={unmatched}   color={unmatched > 0 ? 'text-red-500' : ''} sub={needsReview > 0 ? `${needsReview} awaiting review` : 'no product match'} />
        <StatCard icon={Eye}          label="Total Views" value={fmt(totalViews)} />
        <StatCard icon={DollarSign}   label="Total GMV"   value={fmtCents(totalGmv || null)} sub={`${fmt(totalOrders)} orders`} />
      </div>

      {/* The creator's own answer to "did your sample arrive?" — and, when they
          reported a problem, the place ops closes it out. */}
      <SampleReceiptCard
        hireId={hire.id}
        creatorId={hire.creator_id}
        campaignId={hire.campaign_id}
        creatorName={creatorName}
        hireStatus={hire.status}
        settledAt={hire.settled_at}
      />

      {/* The brand's rating + private memo for THIS hire. Above the timeline:
          "how did it go" belongs with the outcome numbers, not buried under the
          invite history. */}
      <HireFeedbackCard
        hireId={hire.id}
        paymentStatus={hire.payment_status}
        settledAt={hire.settled_at}
        creatorName={creatorName}
      />

      {/* Videos the brand flagged, and what we said back. Renders nothing when
          nothing is flagged, which is the usual case — and sits directly above
          the video table, since that's where the answer gets acted on. */}
      {/* <VideoFlagsCard hireId={hire.id} videos={flaggedVideos} /> */}

      {/* Invite/lifecycle event timeline for this specific hire */}
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">Invite History</h2>
        </div>
        {eventsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <EventTimeline events={events} />
        )}
      </div>

      {/* Negotiation history — only renders anything when this invite was
          countered at least once. Read-only: accepting/declining a counter
          happens on the brand dashboard or in the creator app, not here. */}
      {(counterOffersLoading || counterOffers.length > 0) && (
        <div className="rounded-lg border bg-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold text-sm">Negotiation History</h2>
          </div>
          {counterOffersLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <NegotiationTimeline counters={counterOffers} />
          )}
        </div>
      )}
      {/* Rejection-driven deadline crunch — sits directly above the table so
          it's the first thing visible after rejecting the video that caused
          the shortfall. Renders nothing outside that specific window. */}
      <RejectedVideoDeadlineBanner
        hire={hire}
        delivered={delivered}
        videosRequired={videosRequired}
        lastRejectedAt={lastRejectedAt}
        creatorWindowClosed={creatorWindowClosed}
        onExtended={() => {
          queryClient.invalidateQueries({ queryKey: ['hire-detail', id] });
          queryClient.invalidateQueries({ queryKey: ['hire-videos', id] });
        }}
      />

      {/* Per-product split of this hire's videos. Multi-product campaigns only
          (single-product hires have nothing to break down), and only once there
          is at least one product on record to break down against. Sits directly
          above the table it summarises. */}
      {isMultiProductCampaign && campaignProducts.length > 0 && (
        <VideosByProductCard
          products={campaignProducts}
          videos={allStats}
          bundleProductId={hire.cr_campaigns?.bundle_product_id ?? null}
          bundleProductName={hire.cr_campaigns?.bundle_product_name ?? null}
        />
      )}

      {/* Videos table */}
      <div className="rounded-lg border bg-card">
        {/* Header + search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 border-b px-5 py-3">
          <div>
            <h2 className="font-semibold">Submitted Videos</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {search.trim() ? `${total} result${total !== 1 ? 's' : ''} for "${search}"` : `${allStats.length} video${allStats.length !== 1 ? 's' : ''} submitted for this hire`}
            </p>
          </div>
          {/* The brand's thumbs across the WHOLE hire, not just this page —
              allStats is the unpaginated set. Renders nothing on a hire the
              brand never rated. */}
          <div className="sm:ml-auto">
            <BrandSentimentSummary videos={allStats} />
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by TikTok URL..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Bulk action strip — only while rows are checked, and never on a hire
            whose money is closed (the checkboxes are gone there too). */}
        {!hireLocked && selected.length > 0 && (
          <BulkReviewBar
            videos={selected}
            hireId={id!}
            onClear={() => setSelectedIds([])}
            isViralRemake={hire?.cr_campaigns?.is_viral_remake}
            referenceVideos={hire?.cr_campaigns?.brief_video_urls}
          />
        )}

        {videosLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: pageSize > 5 ? 5 : pageSize }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
            <Video className="h-10 w-10 opacity-25 mb-2" />
            <p className="text-sm font-medium">
              {search.trim() ? 'No videos match your search.' : 'No videos submitted yet.'}
            </p>
          </div>
        ) : (
          <>
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* Select-all covers the reviewable rows on THIS page only —
                        the same rows the checkboxes below are drawn on. The whole
                        column is gone when none of them are reviewable: every
                        video settled, or the hire's money closed.

                        px-0 + a centring flex box because TableCell's base has
                        `[&:has([role=checkbox])]:pr-0`, which would otherwise
                        park the box a padding-width left of the column's middle,
                        out of line with the ticks below it. */}
                    {showSelect && (
                      <TableHead className="w-10 px-0 ps-3">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={allSelected ? true : selected.length > 0 ? 'indeterminate' : false}
                            onCheckedChange={toggleAll}
                            aria-label={allSelected ? 'Clear selection' : 'Select all reviewable videos on this page'}
                          />
                        </div>
                      </TableHead>
                    )}
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>TikTok URL</TableHead>
                    <SortHead field="product_match"     label="Product Match"  active={sortField === 'product_match'}     dir={sortDir} onSort={handleSort} />
                    <TableHead>Submitted Product</TableHead>
                    {/* Flag feature retired 2026-09-28 — the brand can no
                        longer raise a flag, so this column has nothing left
                        to show. Commented out, not deleted, along with its
                        cell below, in case it comes back.
                        Brand flag before Review: what the brand said comes
                        before what we decide about it. */}
                    {/* <TableHead>Brand Flag</TableHead> */}
                    <TableHead>Admin Flag</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead className="whitespace-nowrap">Date Reviewed</TableHead>
                    <SortHead field="views"             label="Views"          active={sortField === 'views'}             dir={sortDir} onSort={handleSort} align="text-right" />
                    <SortHead field="gmv_cents"         label="GMV"            active={sortField === 'gmv_cents'}         dir={sortDir} onSort={handleSort} align="text-right" />
                    <SortHead field="orders"            label="Orders"         active={sortField === 'orders'}            dir={sortDir} onSort={handleSort} align="text-right" />
                    <SortHead field="units_sold"        label="Units Sold"     active={sortField === 'units_sold'}        dir={sortDir} onSort={handleSort} align="text-right" />
                    <SortHead field="submitted_at"      label="Submitted"      active={sortField === 'submitted_at'}      dir={sortDir} onSort={handleSort} />
                    <TableHead>Last Synced</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {videos.map((v, i) => (
                    // Flag feature retired 2026-09-28 — the brand-flag tint
                    // is commented out below, not deleted, in case it comes
                    // back.
                    // An unanswered brand flag outranks the product-match tint:
                    // a matched video the brand has objected to must not read
                    // as a quiet green "all fine" row.
                    <TableRow key={v.id} className={
                      /* isFlagActive(v) ? FLAG_STATUS_META.active.row
                      : */ v.product_match === false ? 'bg-red-50/40'
                      : v.product_match === true ? 'bg-green-50/30'
                      : ''
                    }>
                      {showSelect && (
                        <TableCell className="w-10 px-0 ps-3">
                          {/* An empty cell on a row that can't be reviewed —
                              already decided, or removed from TikTok — so the
                              column stays aligned around the rows that can. */}
                          <div className="flex items-center justify-center">
                            {isBulkSelectable(v, hireLocked) && (
                              <Checkbox
                                checked={selectedIds.includes(v.id)}
                                onCheckedChange={() => toggleOne(v.id)}
                                aria-label={`Select video ${(page - 1) * pageSize + i + 1} for bulk review`}
                              />
                            )}
                          </div>
                        </TableCell>
                      )}

                      <TableCell className="text-muted-foreground text-xs">
                        {(page - 1) * pageSize + i + 1}
                      </TableCell>

                      <TableCell className="max-w-[220px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={v.tiktok_video_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline text-xs flex items-center gap-1 max-w-[200px] truncate"
                              >
                                <ExternalLink className="h-3 w-3 shrink-0" />
                                <span className="truncate">{v.tiktok_video_url}</span>
                              </a>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs break-all text-xs">
                              {v.tiktok_video_url}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>

                      <TableCell>
                        {v.product_match === true ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 rounded-full px-2 py-0.5">
                            <CheckCircle2 className="h-3 w-3" /> Matched
                          </span>
                        ) : v.product_match === false ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            <XCircle className="h-3 w-3" /> Not Matched
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Pending</span>
                        )}
                      </TableCell>

                      <TableCell className="max-w-[180px]">
                        {(() => {
                          // Videos submitted before submitted_product_id/name existed
                          // have no backfill for them — fall back to matched_product_id/
                          // name, the only product info we have on record for those.
                          const productId   = v.submitted_product_id   ?? v.matched_product_id;
                          const productName = v.submitted_product_name ?? v.matched_product_name;
                          if (!productId && !productName) {
                            return <span className="text-xs text-muted-foreground">—</span>;
                          }
                          // Shown value capped at a fixed length (not just CSS
                          // ellipsis inside max-w-[180px]) so a long id or name
                          // can never push the column wider — see
                          // truncateProductName. Full value always in the
                          // Tooltip below.
                          const shown = productId || productName || '';
                          return (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs truncate block cursor-default">
                                    {truncateProductName(shown)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {productId && productName ? `Product Name: ${productName}` : shown}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })()}
                      </TableCell>

                      {/* Flag feature retired 2026-09-28 — matches the
                          commented-out "Brand Flag" TableHead above.
                          Commented out, not deleted, in case it comes back. */}
                      {/* <TableCell>
                        {/* Null on every unflagged video — see VideoFlagBadge. * /}
                        <VideoFlagBadge video={v} />
                      </TableCell> */}

                      <TableCell>
                        <AdminVideoFlagCell
                          video={v}
                          hireId={id!}
                          hire={hire}
                          onChanged={() => queryClient.invalidateQueries({ queryKey: ['hire-videos', id] })}
                        />
                      </TableCell>

                      <TableCell>
                        <VideoReviewCell video={v} hireId={id!} hireLocked={hireLocked} campaign={hire?.cr_campaigns} />
                      </TableCell>

                      {/* reviewed_at is stamped by both the brand's review
                          (worker) and BMC's (VideoReviewCell), so it is the
                          date of whichever decision the row carries —
                          approval or rejection. Auto-approvals are labelled
                          as such so the date isn't read as someone signing
                          off. */}
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {v.review_status && v.reviewed_at ? (
                          <span title={format(new Date(v.reviewed_at), 'MMM d, yyyy h:mm a')}>
                            {format(new Date(v.reviewed_at), 'MMM d, yyyy')}
                            {v.review_status === 'rejected'
                              ? <span className="text-red-700"> · rejected</span>
                              : v.review_source === 'auto' && <span className="text-amber-700"> · auto</span>}
                          </span>
                        ) : '—'}
                      </TableCell>

                      <TableCell className="text-right tabular-nums text-sm">{fmt(v.views)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmtCents(v.gmv_cents)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmt(v.orders)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmt(v.units_sold)}</TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(v.submitted_at), 'MMM d, yyyy')}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {v.last_synced_at ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>{format(new Date(v.last_synced_at), 'MMM d, yyyy')}</span>
                              </TooltipTrigger>
                              {v.last_sync_error && (
                                <TooltipContent side="top" className="max-w-xs text-xs text-red-500">
                                  {v.last_sync_error}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>

      {/* Videos removed from TikTok. Deliberately below the table and visually
          muted: this is context, not work. None of these count anywhere — the
          Submitted/Matched/Delivered stats, the payout gate and the refund math
          all exclude them, which is why they can't simply be a row in the table
          above. Renders nothing on the overwhelming majority of hires. */}
      {deletedVideos.length > 0 && (
        <div className="rounded-lg border border-dashed bg-muted/30">
          <div className="border-b border-dashed px-5 py-3">
            <h2 className="font-semibold text-muted-foreground flex items-center gap-2">
              <Ban className="h-4 w-4" />
              Removed from TikTok
              <Badge variant="outline" className="font-normal">{deletedVideos.length}</Badge>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Not counted anywhere — excluded from the stats above, from the payout and from
              the refund. Shown so you can see what the hire lost and when.
              {deletedVideos.some(v => v.review_status === 'approved') && (
                <>
                  {' '}An approved video here had already counted toward delivery;
                  losing it is what reopens a completed hire.
                </>
              )}
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Video</TableHead>
                  <TableHead>Was</TableHead>
                  <TableHead className="whitespace-nowrap">Submitted</TableHead>
                  <TableHead className="whitespace-nowrap">Removed</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">GMV</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deletedVideos.map(v => (
                  <TableRow key={v.id} className="text-muted-foreground">
                    <TableCell>
                      <a
                        href={v.tiktok_video_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm hover:underline line-through decoration-muted-foreground/50"
                      >
                        {v.tiktok_video_id ?? v.tiktok_video_url}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    </TableCell>

                    {/* The decision the video carried when it disappeared. Worth
                        showing plainly: 'approved' is the only value that means
                        this deletion changed the hire's status. */}
                    <TableCell className="text-xs">
                      {v.review_status === 'approved' ? (
                        <span className="font-medium text-amber-600">
                          Approved{v.review_source ? ` (${v.review_source})` : ''}
                        </span>
                      ) : v.review_status === 'rejected' ? (
                        <span>Rejected</span>
                      ) : (
                        <span>Never reviewed</span>
                      )}
                    </TableCell>

                    <TableCell className="text-xs whitespace-nowrap">
                      {format(new Date(v.submitted_at), 'MMM d, yyyy')}
                    </TableCell>

                    {/* deleted_at is when the sync first found it gone, which is
                        also the moment any status revert happened. Pre-dates the
                        column on old rows — fall back to the last sync. */}
                    <TableCell className="text-xs whitespace-nowrap">
                      {v.deleted_at
                        ? format(new Date(v.deleted_at), 'MMM d, yyyy')
                        : v.last_synced_at
                          ? <span title="Exact removal date not recorded">
                              by {format(new Date(v.last_synced_at), 'MMM d, yyyy')}
                            </span>
                          : '—'}
                    </TableCell>

                    {/* Last numbers we ever saw. Historical — the sync stops
                        updating a video it can no longer fetch. */}
                    <TableCell className="text-right tabular-nums text-sm">{fmt(v.views)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtCents(v.gmv_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
